import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  EdgeChange,
  NodeChange,
  ReactFlowProvider,
  applyNodeChanges,
  NodeProps,
  MarkerType,
  getBezierPath,
  getSmoothStepPath,
  EdgeProps,
  BaseEdge,
  useReactFlow,
  useNodesState,
  useEdgesState,
  Panel,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import debounce from 'lodash.debounce';
import toast from 'react-hot-toast';
import { Database, Edit2, Check, X, Clock, Layers, History } from 'lucide-react';

import { useFlowStore, useProjectStore, useUIStore, useThemeStore } from '../../store';
import Logo from '../common/Logo';
import { ASSISTANT_NAME } from '../../constants/branding';
import { useVersionStore } from '../../store/versionStore';
import { useTutorialStore } from '../../store/tutorialStore';
import EntityHistoryTab from '../history/EntityHistoryTab';
import { useWorkflowStore, defaultExecution } from '../../store/workflowStore';
import { useWorkflow } from '../../hooks/queries/useWorkflow';
import { useWorkflowSteps } from '../../hooks/queries/useWorkflowSteps';
import { useExecution, WorkflowExecutionSnapshot } from '../../hooks/queries/useExecution';
import { useUpdateWorkflow } from '../../hooks/queries/useWorkflowMutations';
import { useCreateStep, useUpdateStep } from '../../hooks/queries/useStepMutations';
import { useUpdateStepPosition } from '../../hooks/queries/useUpdateStepPosition';
import { useRenameWorkflow } from '../../hooks/queries/useRenameMutations';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import { useApiKeys } from '../../hooks/queries/useApiKeys';
import { computeMissingKeyProviders } from '../../utils/byokMissingKeys';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../hooks/queries/queryKeys';
import { buildFlowGraph } from '../../utils/buildFlowGraph';
import { useRunStatus } from '../../hooks/useRunStatus';
import { useEntityEvent } from '../../hooks/useEntityEvent';
import { useQueue, clearCachedQueue } from '../../hooks/useQueue';
import ContextualHelpIcon from '../help/ContextualHelpIcon';
import { useAssistantContext } from '../../contexts/AssistantContext';
import ComponentLibrary from './ComponentLibrary';
import PropertiesPanel from './PropertiesPanel';
import ValidationPanel from './ValidationPanel';
import { WorkflowStep, Agent, Model, Project, StepType, RunInstance, RunResult, RunStatus, SessionMessage, SessionInfo, Schedule, Queue, CreateAgentForm, DeliveryChannel, ChannelAvailability } from '../../types';
import apiService from '../../services/api';
import LogManagementPanel from './LogManagementPanel';
import OutputDisplayModal from './OutputDisplayModal';
import InlineOutputDisplay from './InlineOutputDisplay';
import AgentFormModal from '../agents/AgentFormModal';
import ViewModeSwitcher, { ViewMode } from './ViewModeSwitcher';
import ChatInterface from './ChatInterface';
import ClearSessionButton from './ClearSessionButton';
import { ScheduleModal } from '../Schedule';
import WhatsAppNotificationToggle from './WhatsAppNotificationToggle';
import AutoSummarizeToggle from './AutoSummarizeToggle';
import DeliveryChannelSetting from './DeliveryChannelSetting';

// Custom node types
import AgentNode from './nodes/AgentNode';
import UserPromptNode from './nodes/UserPromptNode';
import ConditionalNode from './nodes/ConditionalNode';
import StartNode from './nodes/StartNode';
import StepConfigurationModal from './StepConfigurationModal';
import AddNode from './nodes/AddNode';
import AutoPromptNode from './nodes/AutoPromptNode';
import QueueWriteNode from './nodes/QueueWriteNode';
import QueueReadNode from './nodes/QueueReadNode';
import FilterNode from './nodes/FilterNode';
import RDAPLookupNode from './nodes/RDAPLookupNode';
import EndNode from './nodes/EndNode';
import FlowContent from './FlowContent';
import NodeContextMenu from './NodeContextMenu';
import OverlappingRunsDialog, { OverlappingRunAction } from './OverlappingRunsDialog';
import { useLayoutHistory } from '../../hooks/useLayoutHistory';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import {
  getLayoutedElements,
  getLayoutedElementsWithRouting,
  snapToGrid,
  calculateNewNodePosition,
  alignNodes,
  distributeNodes,
  optimizeEdgeRouting,
  determineEdgeType,
  selectEdgeHandles,
  GRID_SIZE,
  LayoutDirection,
  getSnakeLayoutedElementsWithRouting,
} from '../../utils/layoutEngine';
import Tooltip from '../common/Tooltip';

// Custom edge with arrowhead offset
function OffsetArrowEdge({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd }: EdgeProps) {
  // Offset the arrowhead by 16px (match or exceed handle offset)
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const len = Math.sqrt(dx * dx + dy * dy);
  const offset = 16;
  const ratio = (len - offset) / len;
  const newTargetX = sourceX + dx * ratio;
  const newTargetY = sourceY + dy * ratio;
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX: newTargetX,
    targetY: newTargetY,
    targetPosition,
  });
  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} />
    </>
  );
}

// Enhanced feedback loop edge with distinctive styling
function FeedbackLoopEdge({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, data, style }: EdgeProps) {
  const isDark = useThemeStore((s) => s.resolvedTheme === 'dark');
  // Canvas-matching background so the halo / label blend into the React Flow canvas in either theme
  const canvasBg = isDark ? '#1A1A1A' : '#ffffff';
  // Use getSmoothStepPath for 90-degree bends with rounded corners for better visual distinction
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 20, // Rounded corners for better visual distinction
  });

  return (
    <>
      {/* Background path for better visibility */}
      <BaseEdge
        path={edgePath}
        style={{
          ...style,
          strokeWidth: (typeof style?.strokeWidth === 'number' ? style.strokeWidth : 2) + 2,
          stroke: canvasBg,
          opacity: 0.8
        }}
      />
      {/* Main feedback loop path */}
      <BaseEdge 
        path={edgePath} 
        markerEnd={markerEnd} 
        style={{
          ...style,
          strokeDasharray: '8,4', // Dashed line pattern for feedback loops
          strokeLinecap: 'round',
        }} 
      />
      
      {/* Enhanced label with background */}
      {data?.label && (() => {
        const labelText = String(data.label);
        const labelWidth = Math.max(50, labelText.length * 6.5 + 16);
        return (
          <g>
            {/* Label background */}
            <rect
              x={labelX - labelWidth / 2}
              y={labelY - 10}
              width={labelWidth}
              height={20}
              fill={canvasBg}
              stroke={style?.stroke || '#6b7280'}
              strokeWidth={1}
              rx={10}
              className="pointer-events-none"
            />
            {/* Label text */}
            <text
              x={labelX}
              y={labelY + 4}
              style={{
                fontSize: 11,
                fontWeight: 700,
                fill: style?.stroke || '#6b7280',
              }}
              textAnchor="middle"
              className="pointer-events-none select-none"
            >
              {data.label}
            </text>
          </g>
        );
      })()}

      {/* Loop indicator - only for actual feedback loops, not exit edges */}
      {!data?.isExit && (
        <g>
          <circle
            cx={labelX + 30}
            cy={labelY}
            r="8"
            fill={style?.stroke || '#6b7280'}
            className="pointer-events-none"
          />
          <path
            d={`M ${labelX + 26} ${labelY - 2}
                L ${labelX + 30} ${labelY + 2}
                L ${labelX + 34} ${labelY - 2}`}
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            className="pointer-events-none"
          />
          <circle
            cx={labelX + 30}
            cy={labelY + 3}
            r="1"
            fill="white"
            className="pointer-events-none"
          />
        </g>
      )}
    </>
  );
}

/**
 * Select feedback loop handles based on relative position of source and target nodes.
 */
function selectFeedbackHandles(sourceNode: Node, targetNode: Node): { sourceHandle: string; targetHandle: string } {
  const dx = targetNode.position.x - sourceNode.position.x;
  const dy = targetNode.position.y - sourceNode.position.y;

  if (Math.abs(dx) > Math.abs(dy)) {
    return { sourceHandle: 'feedback-out-left', targetHandle: 'feedback-in-right' };
  }
  if (dy > 0) {
    return { sourceHandle: 'feedback-out-left', targetHandle: 'feedback-in-top' };
  }
  return { sourceHandle: 'feedback-out-top', targetHandle: 'feedback-in-top' };
}

/**
 * Select handles for exit edges from conditional to EndNode based on position.
 */
function selectExitHandles(sourceNode: Node, targetPos: { x: number; y: number }): { sourceHandle: string; targetHandle: string } {
  // Use source node center (not top-left) for accurate direction calculation
  const sourceW = sourceNode.width || 288; // w-72 default
  const sourceH = sourceNode.height || 150;
  const dx = targetPos.x - (sourceNode.position.x + sourceW / 2);
  const dy = targetPos.y - (sourceNode.position.y + sourceH / 2);

  if (Math.abs(dy) >= Math.abs(dx)) {
    return { sourceHandle: dy >= 0 ? 'bottom' : 'top', targetHandle: dy >= 0 ? 'top' : 'bottom' };
  }
  return { sourceHandle: dx >= 0 ? 'right' : 'left', targetHandle: dx >= 0 ? 'left' : 'right' };
}

/**
 * Helper function to find the actual final step result from an array of run results.
 * Uses multiple criteria since step_order alone may not be reliable:
 * 1. Prefer highest step_order
 * 2. If step_order is the same, prefer later completed_at timestamp
 * 3. If timestamps are the same, prefer higher run_result_id
 */
function findFinalResult(runResults: RunResult[]): RunResult | undefined {
  if (!runResults || runResults.length === 0) return undefined;

  return runResults.reduce((max, r) => {
    const maxStepOrder = max.step_order || 0;
    const rStepOrder = r.step_order || 0;

    // Primary: compare step_order
    if (rStepOrder > maxStepOrder) return r;
    if (rStepOrder < maxStepOrder) return max;

    // Secondary: if step_order is the same, prefer the one with later completed_at
    const maxCompletedAt = max.completed_at ? new Date(max.completed_at).getTime() : 0;
    const rCompletedAt = r.completed_at ? new Date(r.completed_at).getTime() : 0;
    if (rCompletedAt > maxCompletedAt) return r;
    if (rCompletedAt < maxCompletedAt) return max;

    // Tertiary: prefer higher run_result_id (usually later in sequence)
    const maxId = parseInt(String(max.run_result_id), 10) || 0;
    const rId = parseInt(String(r.run_result_id), 10) || 0;
    if (rId > maxId) return r;

    return max;
  }, runResults[0]);
}

/** Small helper to copy raw data text with reliable fallback */
const CopyRawDataButton: React.FC<{ data: any; formatLogData: (d: any) => string }> = ({ data, formatLogData }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const text = String(formatLogData(data));
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
    document.body.removeChild(textarea);
  };

  return (
    <button
      type="button"
      onMouseDown={handleCopy}
      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

const WorkflowEditor: React.FC = () => {
  // Suppress ResizeObserver loop errors
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  const { projectId, workflowId } = useParams<{
    projectId: string;
    workflowId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateWithContext } = useContextualNavigation();
  // AC-1634: latch so the one-time auto-layout (signalled by the assistant via
  // navigation state) fires at most once per WorkflowEditor mount and never on
  // subsequent re-renders or in-app navigations.
  const autoLayoutDoneRef = useRef(false);
  const { setContext } = useAssistantContext();
  

  const {
    nodes,
    edges,
    selectedNode,
    selectedEdge,
    setNodes,
    setEdges,
    setSelectedNode,
    setSelectedEdge,
    validationErrors,
    clear,
    isModelModalOpen,
    setIsModelModalOpen,
  } = useFlowStore();

  // AC-1401: React Query reads. The hooks own caching, dedup, and
  // stale-while-revalidate — replacing the AC-1378 / AC-1381 / AC-1386
  // workarounds in `useFlowStore.loadWorkflow`. The hooks return cached
  // data synchronously on remount with the same workflow ID, so the
  // "Loading workflow..." flash on nav-back is gone naturally.
  const workflowQuery = useWorkflow(workflowId);
  const workflowStepsQuery = useWorkflowSteps(workflowId);

  // AC-1413 (P5.2): `isLoading` used to come from `useFlowStore`, but the
  // legacy `loadWorkflow` body that set it was deleted in AC-1401. The
  // residual store field was permanently `false` and three editor sites
  // (seed-undo-history effect, node-click guard, main "Loading
  // workflow..." render branch) still read it. Anchoring the loading
  // state to the RQ hooks keeps those guards meaningful: they trip
  // exactly when the workflow detail or its steps have not yet
  // resolved, which is the same condition the legacy code intended to
  // gate against.
  const isLoading = workflowQuery.isLoading || workflowStepsQuery.isLoading;
  // useExecution populates the RQ cache slot for this workflow's execution
  // data. We do not consume `data` directly here — the legacy
  // `useWorkflowStore.loadExecution` is still the canonical source for
  // derived UI state (progress, currentStep, polling). See TD section 5.2:
  // polling stays in workflowStore for now. AC-1402+ will migrate it.
  useExecution(workflowId);

  // AC-1406: Mutation hooks. Replace the legacy `apiService.* + loadWorkflow`
  // pattern with hook-based mutations whose `onSuccess` invalidates the right
  // RQ keys (closing the AC-1390/1391/1394 stale-modal-data class of bugs).
  // See `useWorkflowMutations.ts`, `useStepMutations.ts`,
  // `useUpdateStepPosition.ts`, and `useRenameMutations.ts` for the
  // invalidation contracts.
  const renameWorkflowMutation = useRenameWorkflow();
  const updateWorkflowMutation = useUpdateWorkflow();
  const createStepMutation = useCreateStep(workflowId ?? '');
  const updateStepMutation = useUpdateStep(workflowId ?? '');
  const updateStepPositionMutation = useUpdateStepPosition(workflowId ?? '');
  // queryClient is used for invalidation in legacy callbacks (e.g. version
  // history's onEntityChanged) where there isn't a corresponding mutation
  // hook — those paths invalidate the workflow detail key explicitly so the
  // canvas re-renders after an external change.
  const queryClient = useQueryClient();

  // AC-1412 / AC-1377 P5.1: project agents and models flow through React
  // Query (`useAgents` / `useModels`) instead of `useProjectStore.agents` /
  // `useProjectStore.models`. The agent and model mutations from the editor
  // (handled inside StepConfigurationModal and AgentFormModal) invalidate
  // these keys via `useAgentMutations` / `useModelMutations`, so manual
  // `loadAgents()` / `loadModels()` reloads are no longer needed.
  const { currentProject } = useProjectStore();
  const agentsQuery = useAgents(currentProject?.project_id);
  const modelsQuery = useModels(currentProject?.project_id);
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const { componentLibraryOpen, propertiesPanelOpen, setLastWorkflow, openAssistant } = useUIStore();
  const { loadEntityHistory, entityHistory } = useVersionStore();
  const { status: tutorialStatus, currentStep, advanceStep } = useTutorialStore();
  // Remove async workflow store calls that don't have backend support yet
  // const {
  //   startWorkflow,
  //   getExecution,
  //   connectRealTime,
  //   disconnectRealTime
  // } = useWorkflowStore();

  const [isValidating, setIsValidating] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [stepModalContext, setStepModalContext] = useState<{ fromNodeId: string; isEditing?: boolean; parentStepType?: string | null } | null>(null);

  // AC-1401: Workflow name + description are derived from the React Query
  // cache. The hook returns cached data synchronously on remount, so the
  // header no longer needs the AC-1381 `allWorkflows` / `lastWorkflow` seed
  // workaround — that path is gone, replaced by RQ's stale-while-revalidate.
  //
  // The fallback string `''` matches the legacy initial state (empty until
  // loaded). The `'Workflow'` and `'this workflow'` placeholders shown in
  // the header / dialogs handle the empty case; consumers that need a
  // non-empty name (e.g. dialog titles) keep their existing fallback.
  const workflowName = workflowQuery.data?.name ?? '';
  const workflowDescription = workflowQuery.data?.description ?? '';
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [tempName, setTempName] = useState<string>('');
  const [tempDescription, setTempDescription] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('workflow');
  const [isSaving, setIsSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Refs for auto-generated virtual nodes/edges (EndNodes, feedback loops, exit edges)
  // Used by layout handlers which are declared before the useMemo that computes these
  const autoEndNodesRef = useRef<Node[]>([]);
  const autoEdgesRef = useRef<Edge[]>([]);

  // Stores layout-computed EndNode positions so the memo uses them instead of hardcoded offsets
  const layoutEndNodePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  // Stores layout-computed edge handles for auto-generated edges so the memo preserves them
  const layoutEdgeHandlesRef = useRef<Map<string, { sourceHandle: string; targetHandle: string }>>(new Map());
  // Managed EndNode state: proper React state so React Flow can track them during drag.
  // Synced from autoEndNodes on topology change; updated via applyNodeChanges for drag/dimensions.
  const [managedEndNodes, setManagedEndNodes] = useState<Node[]>([]);

  // Layout control states
  const [gridEnabled, setGridEnabled] = useState(false);
  const [snapToGridEnabled, setSnapToGridEnabled] = useState(true);
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);

  // Context menu state
  const [contextMenuNode, setContextMenuNode] = useState<Node | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);

  // Layout history for undo/redo
  const { pushState, undo, redo, canUndo, canRedo, clearHistory } = useLayoutHistory();

  // Ref keeps current managedEndNodes accessible without adding it to every callback's deps.
  const managedEndNodesRef = useRef<Node[]>([]);
  managedEndNodesRef.current = managedEndNodes;

  /** Snapshot current EndNode positions for undo history. */
  const getEndNodePositionSnapshot = useCallback((): Record<string, { x: number; y: number }> | undefined => {
    const arr = managedEndNodesRef.current;
    if (arr.length === 0) return undefined;
    const positions: Record<string, { x: number; y: number }> = {};
    arr.forEach(n => { positions[n.id] = { x: n.position.x, y: n.position.y }; });
    return positions;
  }, []);

  // Effect to ensure current project is set when navigating directly to workflow editor
  useEffect(() => {
    const ensureCurrentOperation = async () => {
      if (!projectId) return;
      
      // If currentProject is not set or doesn't match the URL, load and set it
      if (!currentProject || String(currentProject.project_id) !== projectId) {
        try {
          // Load projects if not already loaded
          const { projects: ops, loadProjects, setCurrentOperation } = useProjectStore.getState();
          if (ops.length === 0) {
            await loadProjects();
          }

          // Find and set the current project
          const project = useProjectStore.getState().projects.find((p: Project) => String(p.project_id) === projectId);
          if (project) {
            setCurrentOperation(project);
          }
        } catch (err) {
          console.error('Error loading project:', err);
        }
      }
    };

    ensureCurrentOperation();
  }, [projectId, currentProject]);

  // AC-1412 / AC-1377 P5.1: Project agents and models are now subscribed
  // via `useAgents` / `useModels` (above). React Query handles the fetch
  // when the projectId changes; the previous manual `loadAgents()` /
  // `loadModels()` effect is removed.

  // Set assistant context when workflow is loaded
  useEffect(() => {
    if (workflowId && projectId) {
      setContext({
        contextType: 'orchestration',
        contextId: parseInt(workflowId),
        projectId: parseInt(projectId)
      });
    } else if (projectId) {
      setContext({
        contextType: 'project',
        projectId: parseInt(projectId)
      });
    } else {
      setContext({
        contextType: 'general'
      });
    }
  }, [workflowId, projectId, setContext]);

  // Seed undo history once nodes are loaded for this workflow.
  // IMPORTANT: must check !isLoading to avoid seeding with the temporary start node
  // created by the "ensure start node exists" effect (line ~604) before loadWorkflow completes.
  const seededWorkflowRef = useRef<string | null>(null);
  useEffect(() => {
    if (workflowId && nodes.length > 0 && !isLoading && seededWorkflowRef.current !== workflowId) {
      seededWorkflowRef.current = workflowId;
      pushState({ nodes, edges, endNodePositions: getEndNodePositionSnapshot() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, nodes.length, isLoading]);

  // AC-1401: Workflow-id change side effects.
  //
  // Reads (workflow detail, steps, execution snapshot) now flow through
  // React Query hooks above — see `workflowQuery`, `workflowStepsQuery`,
  // and the `useExecution` call. This effect is left with the side effects
  // that don't belong to a hook subscription:
  //
  //   1. Reset the layout-history bookkeeping for the new workflow.
  //   2. Clear ephemeral selection so navigating between workflows does
  //      not pop a stale modal back open.
  //   3. Kick off `useWorkflowStore.loadExecution` so the polling lifecycle
  //      starts. Polling stays in the workflow store for now (TD 5.2);
  //      AC-1402+ may move it.
  //
  // The canvas seed (nodes/edges) is wired by a separate effect below that
  // runs whenever the queries' data changes.
  useEffect(() => {
    clearHistory();
    seededWorkflowRef.current = null;

    if (!workflowId) {
      clear();
      return;
    }

    // AC-1728: clear the canvas graph the instant the workflow id changes so a
    // PREVIOUSLY-open workflow's nodes never linger on screen while the new
    // workflow's data loads. This is most visible when opening a workflow the
    // assistant just CREATED server-side (it is not in the React Query cache, so
    // its detail/steps fetch is not synchronous) — without this clear the canvas
    // showed the prior workflow's nodes under the new workflow's name. The seed
    // effect below repopulates from the new workflow's steps once its queries
    // resolve (cached → next render; server-created → when fetched).
    setNodes([]);
    setEdges([]);

    // Reset selection on workflow change — prevents stale-selection ghost
    // states when nav-ing A → B → A.
    setSelectedNode(null);
    setSelectedEdge(null);

    // AC-1377 P5 follow-up: do NOT call loadExecution here.
    // useExecution(workflowId) above already fetches /run-instances +
    // /results into React Query and into the workflowStore's executions
    // map (via the cache mirror). Calling loadExecution here in addition
    // to useExecution doubled every fetch on each mount and, with React
    // StrictMode double-invoking, quadrupled them — exhausting the
    // browser's 6-connection-per-host limit and surfacing as 120s
    // timeouts on /run-instances during heavy navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  // AC-1660 (epic AC-1653): when the assistant confirms an UPDATE to the
  // currently-open orchestration, refresh the canvas by invalidating this
  // workflow's React-Query key. AssistantChat dispatches an `assistant-action`
  // window CustomEvent with detail = { action, projectId, agentId, workflowId,
  // success }; we only act when the action succeeded AND it targets THIS
  // workflow (a background update to a different orchestration must not thrash
  // the open editor's cache). The hierarchical workflow key cascades to the
  // steps + execution sub-keys (see queryKeys.ts), so the canvas re-seeds from
  // fresh data. Mirrors the listener pattern in ProjectDashboard / AgentsPage.
  useEffect(() => {
    const handleAssistantAction = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        workflowId?: number | string;
        success?: boolean;
      };
      const { workflowId: actionWorkflowId, success } = detail || {};
      if (
        success &&
        workflowId &&
        actionWorkflowId != null &&
        Number(actionWorkflowId) === Number(workflowId)
      ) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.workflow(workflowId),
        });
        // AC-1662: an assistant update can edit a step's AGENT (instructions),
        // which lives in the project-agents cache, not the workflow/steps key.
        // Invalidate it too so the canvas step summary and the Edit Agent
        // dialog reflect the new value without needing a manual refresh.
        if (projectId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.projectAgents(projectId),
          });
          // AC-1696: an assistant update can create/edit a structured-output
          // SCHEMA (the Model entity), which AgentNode resolves for the canvas
          // schema modal from the project-models cache (useModels). Invalidate
          // it too so a confirmed schema create/edit reflects without a manual
          // refresh.
          queryClient.invalidateQueries({
            queryKey: queryKeys.projectModels(projectId),
          });
        }
      }
    };

    window.addEventListener('assistant-action', handleAssistantAction as EventListener);
    return () => {
      window.removeEventListener('assistant-action', handleAssistantAction as EventListener);
    };
  }, [workflowId, projectId, queryClient]);

  // AC-1401: Once the workflow detail loads, sync the local form-state for
  // the inline rename / description editors and the auto-summarize toggle,
  // and record the workflow in the breadcrumb store so other pages can
  // back-link with the right name.
  //
  // This effect runs separately from the canvas seed effect because these
  // values only depend on `workflowQuery.data`, while the canvas seed also
  // depends on `workflowStepsQuery.data` and `agents`.
  useEffect(() => {
    const workflow = workflowQuery.data;
    if (!workflow || !workflowId) return;

    setTempName(workflow.name);
    setTempDescription(workflow.description || '');
    setAutoSummarizeEnabled(workflow.auto_summarize ?? false);

    // Initialize the delivery channel setting from the loaded workflow (AC-1816).
    setDeliveryChannel(workflow.delivery_channel ?? null);
    setDeliveryDestination(workflow.delivery_destination ?? null);
    setDeliveryCredentialId(workflow.delivery_credential_id ?? null);

    if (projectId) {
      setLastWorkflow(workflowId, workflow.name, projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowQuery.data, workflowId, projectId]);

  // AC-1401: Canvas seed effect. When the queries deliver fresh workflow
  // detail + steps + (project-scope) agents, build the React Flow nodes /
  // edges via the pure `buildFlowGraph` helper and write them into the
  // flow store. The store still owns mutable nodes/edges for drag
  // interactions; this effect is the seed only — it runs when query data
  // arrives, not on every render.
  useEffect(() => {
    const workflow = workflowQuery.data;
    const steps = workflowStepsQuery.data;
    if (!workflow || !steps) return;

    const { nodes: seedNodes, edges: seedEdges } = buildFlowGraph(
      workflow,
      steps,
      agents,
    );
    setNodes(seedNodes);
    setEdges(seedEdges);
    // AC-1694: workflowId is a dep so an A->B switch (same component, no
    // remount, no key) deterministically re-seeds the canvas. Without it the
    // re-seed relied on React Query data-object identity changing, which a
    // cached B (served synchronously within staleTime) never does -> the canvas
    // kept rendering A's nodes until a manual refresh. The `if (!workflow ||
    // !steps) return` guard makes a transient A-steps/B-detail mismatch
    // self-correct once B's steps land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, workflowQuery.data, workflowStepsQuery.data, agents]);


  // Stop execution polling on unmount, but keep flow store cached for fast nav-back.
  // The flow store is overwritten by loadWorkflow when navigating to a different workflow.
  // Reset ephemeral UI state (open modals, selection) so cached state doesn't pop a modal
  // back open when the user returns.
  useEffect(() => {
    const currentWorkflowId = workflowId;

    return () => {
      setIsModelModalOpen(false);
      setSelectedNode(null);
      setSelectedEdge(null);
      if (currentWorkflowId) {
        const { clearExecution } = useWorkflowStore.getState();
        clearExecution(currentWorkflowId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount/unmount

  // Handlers for inline editing.
  //
  // AC-1406: Inline rename / description edits go through optimistic /
  // pessimistic mutation hooks. `useRenameWorkflow` writes optimistically
  // to the workflow detail cache so the title flips immediately, then the
  // server confirms; `useUpdateWorkflow` is pessimistic and used for the
  // longer description field where the optimistic flicker is less
  // critical. Neither handler needs to call `workflowQuery.refetch()` — the
  // mutation hooks invalidate the right RQ keys on success / settle so the
  // header re-renders from cache automatically.
  const handleSaveName = async () => {
    if (!workflowId || tempName.trim() === workflowName) {
      setIsEditingName(false);
      return;
    }

    setIsSaving(true);
    try {
      await renameWorkflowMutation.mutateAsync({
        workflowId,
        name: tempName.trim(),
        description: workflowDescription,
      });
      setIsEditingName(false);
    } catch (error) {
      console.error('Failed to update workflow name:', error);
      // Revert to original name on error
      setTempName(workflowName);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditName = () => {
    setTempName(workflowName);
    setIsEditingName(false);
  };

  const handleSaveDescription = async () => {
    if (!workflowId || tempDescription === workflowDescription) {
      setIsEditingDescription(false);
      return;
    }

    setIsSaving(true);
    try {
      await updateWorkflowMutation.mutateAsync({
        workflowId,
        data: {
          name: workflowName,
          description: tempDescription.trim(),
        },
      });
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Failed to update workflow description:', error);
      // Revert to original description on error
      setTempDescription(workflowDescription);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditDescription = () => {
    setTempDescription(workflowDescription);
    setIsEditingDescription(false);
  };

  // Handler for sending messages in chat mode
  const handleSendMessage = useCallback(async (message: string) => {
    if (!workflowId) return;

    const currentExecution = useWorkflowStore.getState().executions.get(workflowId);
    if (!currentExecution || !currentExecution.runInstance) return;

    try {
      // Load run results if they're not available
      let runResults = currentExecution.runResults;
      if (!runResults || runResults.length === 0) {
        console.log('[DEBUG] handleSendMessage - Loading run results from API');
        const apiService = (await import('../../services/api')).default;
        const runInstanceId = currentExecution.runInstance.run_instance_id;
        if (runInstanceId) {
          const response = await apiService.getRunInstanceResults(runInstanceId);
          runResults = response.results;
          console.log('[DEBUG] handleSendMessage - Loaded run results:', runResults);
        } else {
          console.error('[DEBUG] handleSendMessage - No runInstanceId available');
          return;
        }
      }
      
      // Find the paused user prompt step by checking for user_prompt steps waiting for input
      console.log('[DEBUG] handleSendMessage - runResults:', runResults);
      console.log('[DEBUG] handleSendMessage - nodes:', nodes);
      
      const pausedUserPrompt = runResults?.find(result => {
        console.log('[DEBUG] handleSendMessage - checking result:', result);
        
        // Find the corresponding workflow step
        const step = nodes.find(node =>
          node.data?.step?.orchestration_step_id === result.orchestration_step_id
        )?.data?.step;
        
        console.log('[DEBUG] handleSendMessage - found step:', step);
        console.log('[DEBUG] handleSendMessage - step_type:', step?.step_type, 'final_output:', result.final_output);

        // Check if this is a user_prompt step that's waiting for input (final_output is null)
        return step?.step_type === 'user_prompt' && !result.final_output;
      });
      
      console.log('[DEBUG] handleSendMessage - pausedUserPrompt:', pausedUserPrompt);

      if (!pausedUserPrompt) {
        console.error('No paused user prompt found');
        return;
      }

      // Update the run result with the user's response
      await apiService.updateRunResult(pausedUserPrompt.run_result_id, {
        final_output: message
      });

      // Continue the workflow execution
      await apiService.runWorkflow(workflowId, currentExecution.runInstance.run_instance_id);

      // Start polling for updates
      useWorkflowStore.getState().startPolling(workflowId, currentExecution.runInstance.run_instance_id);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [workflowId, nodes]);

  // Ensure start node exists only on initial mount
  useEffect(() => {
    if (nodes.length === 0) {
      const startNode: Node = {
        id: 'start-node',
        type: 'start',
        position: { x: 100, y: 100 },
        data: { label: 'Start' },
      };
      setNodes([startNode]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Removed: This useEffect was stopping polling too eagerly, preventing results from loading
  // Polling cleanup is already handled by:
  // 1. clearExecution when workflowId changes (line 319)
  // 2. Cleanup function on unmount (line 336-344)

  // Handle node click for editing
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (isModelModalOpen) return;
    if (isLoading) return; // Prevent modal flash during node reload (e.g., after deletion)
    if (node.id === 'start-node') return;
    if (node.id.startsWith('end-')) return; // Virtual EndNodes are not editable

    setSelectedNode(node.id);
    setSelectedEdge(null);

    // Open edit modal for existing steps
    if (node.data?.step) {
      setStepModalContext({ fromNodeId: node.id, isEditing: true });
      setShowStepModal(true);
    }
  }, [setSelectedNode, setSelectedEdge, isModelModalOpen, isLoading]);

  // Handle right-click context menu on nodes
  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault(); // Prevent browser context menu

    // AC-1289: show the context menu for both user_prompt (convert-to-auto-prompt)
    // and conditional (convert-to-filter) nodes.
    const stepType = node.data?.step?.step_type;
    if (stepType === 'user_prompt' || stepType === 'conditional') {
      setContextMenuNode(node);
      setContextMenuPosition({ x: event.clientX, y: event.clientY });
    }
  }, []);

  // Handle context menu close
  const handleContextMenuClose = useCallback(() => {
    setContextMenuNode(null);
    setContextMenuPosition(null);
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuNode) {
        // Check if click is outside the context menu
        const contextMenuElement = document.querySelector('[role="menu"]');
        if (contextMenuElement && !contextMenuElement.contains(event.target as HTMLElement)) {
          handleContextMenuClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenuNode, handleContextMenuClose]);

  // Handle converting user prompt to auto prompt
  const handleConvertToAutoPrompt = useCallback(async (stepId: string) => {
    if (!workflowId) {
      console.error('Workflow ID is not available');
      throw new Error('Workflow ID is not available');
    }

    try {
      // Close the current modal first to ensure proper unmounting
      setShowStepModal(false);
      setStepModalContext(null);

      // Close context menu if open
      handleContextMenuClose();

      // Call API to convert the step
      const convertedStep = await apiService.convertUserPromptToAutoPrompt(workflowId, stepId);

      // Update the node in the flow
      const convertedNodes = nodes.map((node) => {
        if (node.data?.step?.orchestration_step_id?.toString() === stepId) {
          return {
            ...node,
            type: 'autoPrompt', // Change node type to autoPrompt for proper rendering
            data: {
              ...node.data,
              step: convertedStep,
              label: 'Auto Prompt', // Update label
            },
          };
        }
        return node;
      });
      setNodes(convertedNodes);
      pushState({ nodes: convertedNodes, edges, endNodePositions: getEndNodePositionSnapshot() });

      // Show success toast
      toast.success('Step converted to Auto-Prompt');

      // Reopen modal for the converted step after a brief delay to ensure proper reinitialization
      // Node ID equals step ID (see store/index.ts line 167), so we can use stepId directly
      setTimeout(() => {
        setStepModalContext({ fromNodeId: stepId, isEditing: true });
        setShowStepModal(true);
      }, 100);
    } catch (error) {
      console.error('Failed to convert user prompt to auto prompt:', error);
      toast.error('Failed to convert step to Auto-Prompt');
      throw error; // Re-throw to let the calling component handle the error if needed
    }
  }, [workflowId, setNodes, handleContextMenuClose, nodes, edges, pushState]);

  // AC-1289: Handle converting a conditional step to a filter step.
  const handleConvertToFilter = useCallback(async (stepId: string) => {
    if (!workflowId) {
      console.error('Workflow ID is not available');
      throw new Error('Workflow ID is not available');
    }

    try {
      setShowStepModal(false);
      setStepModalContext(null);
      handleContextMenuClose();

      const converted = await apiService.convertConditionalToFilter(workflowId, stepId);

      const convertedNodes = nodes.map((node) => {
        if (node.data?.step?.orchestration_step_id?.toString() === stepId) {
          return {
            ...node,
            type: 'filter',
            data: {
              ...node.data,
              step: converted,
              label: 'Filter',
            },
          };
        }
        return node;
      });
      setNodes(convertedNodes);
      pushState({ nodes: convertedNodes, edges, endNodePositions: getEndNodePositionSnapshot() });

      toast.success('Step converted to Filter');

      setTimeout(() => {
        setStepModalContext({ fromNodeId: stepId, isEditing: true });
        setShowStepModal(true);
      }, 100);
    } catch (error) {
      console.error('Failed to convert conditional to filter:', error);
      toast.error('Failed to convert step to Filter');
      throw error;
    }
  }, [workflowId, setNodes, handleContextMenuClose, nodes, edges, pushState]);

  // Handle edge selection
  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      setSelectedEdge(edge.id);
      setSelectedNode(null);
    },
    [setSelectedEdge, setSelectedNode]
  );

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      if (!sourceNode || !targetNode) return;

      const { sourceHandle, targetHandle } = selectEdgeHandles(sourceNode, targetNode, nodes);

      const newEdge = {
        ...params,
        id: `edge-${Date.now()}`,
        type: determineEdgeType(sourceNode, targetNode),
        animated: false,
        data: {},
        source: params.source || '',
        target: params.target || '',
        sourceHandle,
        targetHandle,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 24,
          height: 24,
          color: '#3b82f6',
        },
      };
      const newEdges = [...edges, newEdge];
      setEdges(newEdges);
      pushState({ nodes, edges: newEdges, endNodePositions: getEndNodePositionSnapshot() });
    },
    [edges, setEdges, nodes, pushState]
  );

  // In handleNodesChange, persist position changes and update edge handles
  // Position-only updates skip version control to avoid noise in history.
  //
  // AC-1406: Step positions go through `useUpdateStepPosition` (optimistic;
  // cache flips synchronously so dragged nodes feel instant — see hook
  // JSDoc for the snapshot/rollback/settle lifecycle). The start-node lives
  // on the workflow row itself, so it goes through `useUpdateWorkflow`
  // (pessimistic — there's no high-frequency drag for the start node and
  // the position is denormalized onto the workflow detail row, which we
  // don't optimistically merge to avoid widening the optimistic surface).
  // Both hooks invalidate the right RQ keys on settle, so the manual
  // `loadWorkflow` reload that used to follow these calls is gone.
  const persistNodePosition = debounce(async (node: Node) => {
    if (node.id === 'start-node') {
      await updateWorkflowMutation.mutateAsync({
        workflowId: workflowId!,
        data: {
          start_node_position_x: node.position.x,
          start_node_position_y: node.position.y,
          skip_version_control: true, // Don't create version history for position changes
        },
      });
    } else if (node.data && node.data.step && node.data.step.orchestration_step_id) {
      await updateStepPositionMutation.mutateAsync({
        stepId: node.data.step.orchestration_step_id,
        position_x: node.position.x,
        position_y: node.position.y,
      });
    }
  }, 300);

  // Persist all node positions immediately (used for auto-layout and bulk projects)
  // Position-only updates skip version control to avoid noise in history.
  // See `persistNodePosition` JSDoc above for why we route through the
  // mutation hooks rather than direct `apiService.*` calls.
  const persistAllNodePositions = useCallback(async (nodesToPersist: Node[]) => {
    try {
      console.log(`Persisting positions for ${nodesToPersist.length} nodes...`);
      const promises = nodesToPersist.map(async (node) => {
        if (node.id === 'start-node') {
          console.log(`Saving start-node position: x=${node.position.x}, y=${node.position.y}`);
          return updateWorkflowMutation.mutateAsync({
            workflowId: workflowId!,
            data: {
              start_node_position_x: node.position.x,
              start_node_position_y: node.position.y,
              skip_version_control: true,
            },
          });
        } else if (node.data && node.data.step && node.data.step.orchestration_step_id) {
          console.log(`Saving step ${node.data.step.orchestration_step_id} position: x=${node.position.x}, y=${node.position.y}`);
          return updateStepPositionMutation.mutateAsync({
            stepId: node.data.step.orchestration_step_id,
            position_x: node.position.x,
            position_y: node.position.y,
          });
        }
        return Promise.resolve();
      });

      const results = await Promise.all(promises);
      console.log(`Successfully persisted positions for ${nodesToPersist.length} nodes`, results);
    } catch (error) {
      console.error('Error persisting node positions:', error);
      throw error; // Re-throw to make errors visible
    }
  }, [workflowId, updateWorkflowMutation, updateStepPositionMutation]);

  // Update edge handles based on node positions - uses dominant-direction routing with node-crossing avoidance
  const updateEdgeHandles = useCallback((nodes: Node[], edges: Edge[]) => {
    return edges.map(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return edge;

      const { sourceHandle, targetHandle } = selectEdgeHandles(sourceNode, targetNode, nodes);
      const edgeType = determineEdgeType(sourceNode, targetNode);

      if (edge.sourceHandle !== sourceHandle || edge.targetHandle !== targetHandle || edge.type !== edgeType || !edge.markerEnd) {
        return { ...edge, sourceHandle, targetHandle, type: edgeType, markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 24,
          height: 24,
          color: '#3b82f6',
        }};
      }
      return edge;
    });
  }, []);

  // Capture canvas state after a node drag ends (for undo).
  // Always pushes state — even for EndNode-only drags — so that EndNode position
  // changes are captured in the undo history via endNodePositions.
  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node, draggedNodes: Node[]) => {
      const realDraggedNodes = draggedNodes.filter((n) => !n.id.startsWith('end-'));

      // Merge real dragged node positions into the current nodes array
      const updatedNodes = realDraggedNodes.length > 0
        ? (() => {
            const positionMap = new Map(realDraggedNodes.map((n) => [n.id, n.position]));
            return nodes.map((n) =>
              positionMap.has(n.id) ? { ...n, position: positionMap.get(n.id)! } : n
            );
          })()
        : nodes;

      pushState({ nodes: updatedNodes, edges, endNodePositions: getEndNodePositionSnapshot() });
    },
    [nodes, edges, pushState, getEndNodePositionSnapshot]
  );

  // Handle node changes - only recalculate edges for nodes that actually moved
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Separate EndNode changes from real node changes
      const endNodeChanges: NodeChange[] = [];
      const filteredChanges: NodeChange[] = [];
      for (const change of changes) {
        const id = 'id' in change ? change.id : '';
        if (id.startsWith('end-')) {
          endNodeChanges.push(change);
        } else {
          filteredChanges.push(change);
        }
      }

      // Handle EndNode changes: apply position+dimension changes through applyNodeChanges
      // so React Flow can properly track EndNodes during drag (prevents disappearing).
      if (endNodeChanges.length > 0) {
        const safeChanges = endNodeChanges.filter(
          c => c.type === 'position' || c.type === 'dimensions'
        );
        if (safeChanges.length > 0) {
          setManagedEndNodes(prev => {
            const updated = applyNodeChanges(safeChanges, prev);
            // Sync positions to layout ref for persistence across topology changes
            for (const change of safeChanges) {
              if (change.type === 'position' && 'position' in change && change.position) {
                layoutEndNodePositionsRef.current.set(change.id, change.position);
              }
            }
            layoutEdgeHandlesRef.current = new Map();
            return updated;
          });
        }
      }

      if (filteredChanges.length === 0) return;
      setNodes((nds) => {
        let updated = applyNodeChanges(filteredChanges, nds);
        const movedNodeIds = new Set<string>();

        // Apply snap to grid if enabled
        if (snapToGridEnabled) {
          updated = updated.map(node => {
            const hasPositionChange = filteredChanges.some(
              change => change.type === 'position' && change.id === node.id
            );
            if (hasPositionChange) {
              return {
                ...node,
                position: snapToGrid(node.position),
              };
            }
            return node;
          });
        }

        // Move AddNodes along with their parent nodes
        const parentWidth = 288; // w-72 in Tailwind
        const addNodeWidth = 48; // w-12 in Tailwind
        const addNodeOffsetY = 200; // vertical offset below parent

        filteredChanges.forEach(change => {
          if (change.type === 'position') {
            const node = updated.find(n => n.id === change.id);
            const prevNode = nds.find(n => n.id === change.id);
            if (node && prevNode) {
              // Only count as moved if position actually changed by more than 2px
              // This prevents micro-drags during clicks from triggering edge recalculation
              const positionDx = Math.abs(node.position.x - prevNode.position.x);
              const positionDy = Math.abs(node.position.y - prevNode.position.y);
              if (positionDx < 2 && positionDy < 2) {
                return; // Skip - not a meaningful move
              }
              persistNodePosition(node);
              movedNodeIds.add(node.id);
              // Clear layout-computed positions and handles on manual drag so they revert to defaults
              if (layoutEndNodePositionsRef.current.size > 0) {
                layoutEndNodePositionsRef.current = new Map();
              }
              if (layoutEdgeHandlesRef.current.size > 0) {
                layoutEdgeHandlesRef.current = new Map();
              }

              // Find and reposition the AddNode for this parent
              const addNodeId = `add-node-${node.id}`;
              updated = updated.map(n => {
                if (n.id === addNodeId) {
                  movedNodeIds.add(addNodeId);
                  return {
                    ...n,
                    position: {
                      x: node.position.x + (parentWidth / 2) - (addNodeWidth / 2),
                      y: node.position.y + addNodeOffsetY,
                    },
                  };
                }
                return n;
              });
            }
          } else if (change.type === 'select') {
            // Track selected nodes for alignment tools
            const selected = updated.filter(n => n.selected);
            setSelectedNodes(selected);
          }
        });

        // Only recalculate handles for edges connected to nodes that actually moved.
        // This prevents overriding handle assignments from auto-layout projects
        // when unrelated nodes trigger position changes.
        if (movedNodeIds.size > 0) {
          const newEdges = edges.map(edge => {
            // Only recalculate edges where the source or target node moved
            if (!movedNodeIds.has(edge.source) && !movedNodeIds.has(edge.target)) {
              return edge;
            }

            const sourceNode = updated.find(n => n.id === edge.source);
            const targetNode = updated.find(n => n.id === edge.target);
            if (!sourceNode || !targetNode) return edge;

            const { sourceHandle, targetHandle } = selectEdgeHandles(sourceNode, targetNode, updated);
            const edgeType = determineEdgeType(sourceNode, targetNode);

            if (edge.sourceHandle !== sourceHandle || edge.targetHandle !== targetHandle || edge.type !== edgeType || !edge.markerEnd) {
              return { ...edge, sourceHandle, targetHandle, type: edgeType, markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 24,
                height: 24,
                color: '#3b82f6',
              }};
            }
            return edge;
          });
          const isChanged = newEdges.some((e, i) => e !== edges[i]);
          if (isChanged) {
            setEdges(newEdges);
          }
        }
        return updated;
      });
    },
    [setNodes, setEdges, edges, snapToGridEnabled, persistNodePosition]
  );

  // Handle edge changes
  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // React Flow expects you to update the edges array here
      // We'll use the changes to update the store
      // (You may want to use applyEdgeChanges from reactflow for more complex cases)
      // For now, just ignore changes
    },
    []
  );

  // Handle drag over for drop functionality
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop to create new nodes
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = (event.target as Element)
        .closest('.react-flow')
        ?.getBoundingClientRect();

      if (!reactFlowBounds) return;

      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(
        event.dataTransfer.getData('application/nodedata') || '{}'
      );

      if (!type) return;

      let position = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };
      
      // Apply snap to grid if enabled
      if (snapToGridEnabled) {
        position = snapToGrid(position);
      }

      const newNode: Node = {
        id: `node-${Date.now()}`,
        type,
        position,
        data: {
          label: nodeData.label || `New ${type}`,
          type,
          ...nodeData,
        },
      };

      setNodes([...nodes, newNode]);
    },
    [nodes, setNodes, snapToGridEnabled]
  );

  // Validate orchestration
  const validateWorkflow = useCallback(async () => {
    if (!workflowId) return;

    setIsValidating(true);
    try {
      // Call validation API
      // const result = await apiService.validateWorkflow(workflowId);
      // Handle validation results
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setIsValidating(false);
    }
  }, [workflowId]);

  // Layout control handlers
  const handleAutoLayout = useCallback(
    async (direction: LayoutDirection = 'TB') => {
      try {
        // Include virtual EndNodes/edges in layout so dagre positions them properly
        const layoutNodes = [...nodes, ...autoEndNodesRef.current];
        const layoutEdges = [...edges, ...autoEdgesRef.current];
        const layouted = await getLayoutedElementsWithRouting(layoutNodes, layoutEdges, { direction });

        // Save layout-computed EndNode positions and auto-edge handles
        const newPositions = new Map<string, { x: number; y: number }>();
        layouted.nodes.filter(n => n.type === 'end').forEach(n => {
          newPositions.set(n.id, { x: n.position.x, y: n.position.y });
        });
        layoutEndNodePositionsRef.current = newPositions;
        // Also update managed EndNodes with layout-computed positions
        setManagedEndNodes(prev => prev.map(n => {
          const pos = newPositions.get(n.id);
          return pos ? { ...n, position: pos } : n;
        }));

        // Recompute exit/feedback edge handles using post-layout node positions
        // (dagre doesn't modify edges, so pre-layout handles would be stale)
        const newHandles = new Map<string, { sourceHandle: string; targetHandle: string }>();
        layouted.edges.forEach(e => {
          if (e.id.startsWith('exit-edge-') || e.id.startsWith('feedback-loop-')) {
            const sourceNode = layouted.nodes.find(n => n.id === e.source);
            const targetNode = layouted.nodes.find(n => n.id === e.target);
            if (sourceNode && targetNode) {
              if (e.id.startsWith('exit-edge-')) {
                newHandles.set(e.id, selectExitHandles(sourceNode, targetNode.position));
              } else {
                newHandles.set(e.id, selectFeedbackHandles(sourceNode, targetNode));
              }
            }
          }
        });
        layoutEdgeHandlesRef.current = newHandles;

        // Separate real nodes from virtual EndNodes before storing
        const realNodes = layouted.nodes.filter(n => n.type !== 'end');
        const realEdges = layouted.edges.filter(e => !e.id.startsWith('exit-edge-') && !e.id.startsWith('feedback-loop-') && !e.id.startsWith('assistant-auto-loop-'));

        setNodes(realNodes);

        // Update edge handles for proper routing based on new positions
        const updatedEdges = updateEdgeHandles(realNodes, realEdges);
        setEdges(updatedEdges);

        // Push post-layout state so undo restores the pre-layout positions
        pushState({ nodes: realNodes, edges: updatedEdges, endNodePositions: getEndNodePositionSnapshot() });

        // Persist all new positions to the backend immediately
        await persistAllNodePositions(realNodes);
      } catch (error) {
        console.error('Error in handleAutoLayout:', error);
        // Still keep the visual changes even if persistence fails
      }
    },
    [nodes, edges, setNodes, setEdges, pushState, persistAllNodePositions, updateEdgeHandles]
  );

  const handleCompactLayout = useCallback(
    async () => {
      try {
        // Include virtual EndNodes/edges in layout so snake algorithm positions them properly
        const layoutNodes = [...nodes, ...autoEndNodesRef.current];
        const layoutEdges = [...edges, ...autoEdgesRef.current];
        const layouted = await getSnakeLayoutedElementsWithRouting(layoutNodes, layoutEdges);

        // Save layout-computed EndNode positions and auto-edge handles
        const newPositions = new Map<string, { x: number; y: number }>();
        layouted.nodes.filter(n => n.type === 'end').forEach(n => {
          newPositions.set(n.id, { x: n.position.x, y: n.position.y });
        });
        layoutEndNodePositionsRef.current = newPositions;
        // Also update managed EndNodes with layout-computed positions
        setManagedEndNodes(prev => prev.map(n => {
          const pos = newPositions.get(n.id);
          return pos ? { ...n, position: pos } : n;
        }));

        const newHandles = new Map<string, { sourceHandle: string; targetHandle: string }>();
        layouted.edges.forEach(e => {
          if (e.id.startsWith('exit-edge-') || e.id.startsWith('feedback-loop-')) {
            if (e.sourceHandle && e.targetHandle) {
              newHandles.set(e.id, { sourceHandle: e.sourceHandle, targetHandle: e.targetHandle });
            }
          }
        });
        layoutEdgeHandlesRef.current = newHandles;

        // Separate real nodes from virtual EndNodes before storing
        const realNodes = layouted.nodes.filter(n => n.type !== 'end');
        const realEdges = layouted.edges.filter(e => !e.id.startsWith('exit-edge-') && !e.id.startsWith('feedback-loop-') && !e.id.startsWith('assistant-auto-loop-'));

        setNodes(realNodes);
        setEdges(realEdges);

        // Push post-layout state so undo restores the pre-layout positions
        pushState({ nodes: realNodes, edges: realEdges, endNodePositions: getEndNodePositionSnapshot() });

        await persistAllNodePositions(realNodes);
      } catch (error) {
        console.error('Error in handleCompactLayout:', error);
      }
    },
    [nodes, edges, setNodes, setEdges, pushState, persistAllNodePositions]
  );

  // AC-1634 / AC-1638: when the assistant opens a freshly-created workflow it
  // navigates with `state.autoLayout === true`. Run a single compact layout
  // pass, then clear the flag (ref latch + history replace) so it never re-runs
  // on re-render or later navigation. handleCompactLayout persists the new
  // positions, so subsequent opens read the laid-out graph.
  //
  // AC-1638: gate on the FULL graph being seeded. During load a transient
  // Start-only node renders (the "ensure start node exists" effect) before the
  // real steps seed via buildFlowGraph. Firing on that partial graph laid out
  // nothing and latched, leaving the real steps bunched. Only fire once the
  // rendered step-nodes match the loaded step count.
  const loadedStepCount = workflowStepsQuery.data?.length ?? 0;
  const seededStepCount = nodes.filter((n) => (n.data as { step?: unknown })?.step).length;
  useEffect(() => {
    if (autoLayoutDoneRef.current) return;
    if (!location.state?.autoLayout) return;
    if (isLoading) return; // both workflow + steps queries must be settled
    // Wait until every loaded step has a corresponding node on the canvas.
    if (loadedStepCount === 0 || seededStepCount < loadedStepCount) return;

    autoLayoutDoneRef.current = true;
    void handleCompactLayout();
    // Drop the navigation state so a refresh/back never re-triggers the layout.
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.state,
    location.pathname,
    isLoading,
    loadedStepCount,
    seededStepCount,
    handleCompactLayout,
    navigate,
  ]);

  const handleAlignNodes = useCallback(
    async (alignment: 'left' | 'right' | 'center' | 'top' | 'bottom' | 'middle') => {
      const selectedNodeIds = selectedNodes.map(n => n.id);
      if (selectedNodeIds.length < 2) return;

      // Align nodes
      const aligned = alignNodes(nodes, selectedNodeIds, alignment);
      setNodes(aligned);

      // Push post-align state so undo restores the pre-align positions
      pushState({ nodes: aligned, edges, endNodePositions: getEndNodePositionSnapshot() });

      // Persist the new positions to the backend immediately
      const nodesToPersist = aligned.filter(node => selectedNodeIds.includes(node.id));
      await persistAllNodePositions(nodesToPersist);
    },
    [nodes, edges, selectedNodes, setNodes, pushState, persistAllNodePositions]
  );

  const handleDistributeNodes = useCallback(
    async (direction: 'horizontal' | 'vertical') => {
      const selectedNodeIds = selectedNodes.map(n => n.id);
      if (selectedNodeIds.length < 3) return;

      // Distribute nodes
      const distributed = distributeNodes(nodes, selectedNodeIds, direction);
      setNodes(distributed);

      // Push post-distribute state so undo restores the pre-distribute positions
      pushState({ nodes: distributed, edges, endNodePositions: getEndNodePositionSnapshot() });

      // Persist the new positions to the backend immediately
      const nodesToPersist = distributed.filter(node => selectedNodeIds.includes(node.id));
      await persistAllNodePositions(nodesToPersist);
    },
    [nodes, edges, selectedNodes, setNodes, pushState, persistAllNodePositions]
  );

  /** Restore EndNode positions from an undo/redo snapshot. */
  const restoreEndNodePositions = useCallback((endNodePositions?: Record<string, { x: number; y: number }>) => {
    if (!endNodePositions) return;
    setManagedEndNodes(prev => prev.map(n => {
      const pos = endNodePositions[n.id];
      return pos ? { ...n, position: pos } : n;
    }));
    // Also update ref so the next autoEndNodes topology sync preserves these positions
    layoutEndNodePositionsRef.current = new Map(
      Object.entries(endNodePositions).map(([k, v]) => [k, v])
    );
  }, []);

  const handleUndo = useCallback(() => {
    const state = undo();
    if (state) {
      setNodes(state.nodes);
      setEdges(state.edges);
      persistAllNodePositions(state.nodes);
      restoreEndNodePositions(state.endNodePositions);
    }
  }, [undo, setNodes, setEdges, persistAllNodePositions, restoreEndNodePositions]);

  const handleRedo = useCallback(() => {
    const state = redo();
    if (state) {
      setNodes(state.nodes);
      setEdges(state.edges);
      persistAllNodePositions(state.nodes);
      restoreEndNodePositions(state.endNodePositions);
    }
  }, [redo, setNodes, setEdges, persistAllNodePositions, restoreEndNodePositions]);


  // Auto-validate on changes
  useEffect(() => {
    const timer = setTimeout(() => {
      validateWorkflow();
    }, 1000);

    return () => clearTimeout(timer);
  }, [nodes, edges, validateWorkflow]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Ctrl/Cmd + L: Auto-layout
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        handleAutoLayout('TB');
      }
      
      // Ctrl/Cmd + G: Toggle grid
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        setGridEnabled(prev => !prev);
      }
      
      // Ctrl/Cmd + Z: Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      
      // Ctrl/Cmd + Y or Ctrl/Cmd + Shift + Z: Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
      
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleAutoLayout, handleUndo, handleRedo]);

  // Unified '+' handler for all nodes - stable reference to prevent nodeTypes recreation
  const handleAddStepFromNode = useCallback((parentNodeId?: string) => {
    const resolvedParentId = parentNodeId || 'start-node';
    setStepModalContext({
      fromNodeId: resolvedParentId,
      parentStepType: null, // We'll resolve this in the modal
      isEditing: false
    });
    setShowStepModal(true);
  }, []);

  // Handle component click from ComponentLibrary (used for tutorial tracking)
  const handleComponentClick = useCallback((nodeType: string, nodeData: any) => {
    // This handler allows ComponentLibrary to notify us when components are clicked
    // Currently used for tutorial step tracking in ComponentLibrary
    console.log('[DEBUG] Component clicked:', nodeType, nodeData);
  }, []);

  // Fix onAdd functions for add nodes - runs on every render to catch missing functions
  useEffect(() => {
    const addNodesWithoutOnAdd = nodes.filter(node =>
      node.type === 'add' && typeof node.data?.onAdd !== 'function'
    );

    if (addNodesWithoutOnAdd.length > 0) {
      console.log('[DEBUG] WorkflowEditor - Fixing', addNodesWithoutOnAdd.length, 'add nodes without onAdd functions');
      const updatedNodes = nodes.map(node => {
        if (node.type === 'add' && typeof node.data?.onAdd !== 'function') {
          return {
            ...node,
            data: {
              ...node.data,
              onAdd: handleAddStepFromNode.bind(null, node.data?.parentNodeId)
            }
          };
        }
        return node;
      });
      setNodes(updatedNodes);
    }
  });


  // Define node types outside component to prevent recreation
  const nodeTypes = useMemo(() => ({
    agent: AgentNode,
    userPrompt: UserPromptNode,
    conditional: ConditionalNode,
    start: StartNode,
    add: AddNode,
    autoPrompt: AutoPromptNode,
    queueWrite: QueueWriteNode,
    queueRead: QueueReadNode,
    filter: FilterNode,  // AC-1273: filter step visualization
    rdapLookup: RDAPLookupNode,  // AC-1444 (epic AC-1434): RDAP domain availability step
    end: EndNode,
  }), []);

  const edgeTypes = useMemo(() => ({
    offsetArrow: OffsetArrowEdge,
    feedbackLoop: FeedbackLoopEdge,
  }), []);

  // Memoize default edge options to prevent unnecessary re-renders
  const defaultEdgeOptions = useMemo(() => ({
    type: 'straight', // Use straight edges for perfect arrow alignment
    style: { strokeWidth: 2, stroke: '#3b82f6' },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 20,
      height: 20,
      color: '#3b82f6',
    }
  }), []);

  // Check if workflow has assistant steps
  const hasAssistantSteps = useMemo(() => {
    return nodes.some(node =>
      node.type === 'agent' &&
      node.data?.step?.intent === 'assistant'
    );
  }, [nodes]);

  // AC-473: Check if workflow has a valid initiator step (required for execution/scheduling)
  // Valid initiator step types: user_prompt, auto_prompt, queue_read (with no previous_step_id)
  const hasInitiatorStep = useMemo(() => {
    const initiatorStepTypes = ['user_prompt', 'auto_prompt', 'queue_read'];
    return nodes.some(node => {
      const step = node.data?.step;
      if (!step) return false;
      // Must be an initiator step type AND have no previous step (i.e., is the starting point)
      return initiatorStepTypes.includes(step.step_type) && !step.previous_step_id;
    });
  }, [nodes]);

  // AC-504: Check if workflow has queue_read as its first step
  // Queue-read workflows are triggered by their source queue's schedule, not their own
  const hasQueueReadAsFirstStep = useMemo(() => {
    // Find the first step (the one with no previous_step_id)
    const firstStep = nodes.find(node => {
      const step = node.data?.step;
      return step && !step.previous_step_id;
    });
    return firstStep?.data?.step?.step_type === 'queue_read';
  }, [nodes]);

  // AC-481: Resolve source queue id from the first step's config (when the
  // workflow starts with a queue_read step) and fetch via the cached hook so
  // navigating back to the editor doesn't re-trigger a multi-second flash.
  const sourceQueueId = useMemo(() => {
    if (!hasQueueReadAsFirstStep) return undefined;
    const firstStep = nodes.find(node => {
      const step = node.data?.step;
      return step && !step.previous_step_id;
    });
    const config = firstStep?.data?.step?.config as { source_queue_id?: number } | undefined;
    return config?.source_queue_id;
  }, [hasQueueReadAsFirstStep, nodes]);

  const { queue: sourceQueue, isLoading: isLoadingSourceQueue } = useQueue(sourceQueueId);

  // AC-1369: clear the cached source queue immediately on external delete so
  // the workflow header doesn't keep displaying the deleted queue's name.
  useEntityEvent({
    entity: 'queue',
    id: sourceQueueId,
    handler: (event) => {
      if (event.kind === 'deleted' && sourceQueueId !== undefined) {
        clearCachedQueue(sourceQueueId);
      }
    },
  });

  // Check if the workflow is executable (has at least one initiator step)
  const canExecuteOrSchedule = hasInitiatorStep && nodes.length > 0;

  // ---------------------------------------------------------------------
  // AC-1517 (epic AC-1493 P7D): cannot-run banner for missing BYOK keys.
  //
  // When the deployment is in BYOK mode and the workflow contains agents
  // whose model belongs to a provider the current user has NOT configured,
  // running will fail at the agent-runner level. We surface this up-front
  // as a yellow banner above the Execute button, listing the providers
  // that need keys and linking to /settings/api-keys.
  //
  // The check is opt-in: in non-BYOK mode (server-key) the banner stays
  // hidden. We also stay silent until both the llm catalog AND the user
  // key list have loaded — otherwise the banner would flash on every page
  // open while RQ resolves both queries.
  // ---------------------------------------------------------------------
  const { llmCatalog, llmByokMode } = useProjectStore();
  const apiKeysQuery = useApiKeys({ enabled: llmByokMode });

  const missingKeyProviders = useMemo(() => {
    if (!llmByokMode) return [] as string[];
    if (!llmCatalog || llmCatalog.length === 0) return [];
    if (!apiKeysQuery.data) return [];

    const configured = new Set<string>(
      apiKeysQuery.data.filter((k) => k.has_key).map((k) => k.provider),
    );

    // Map model id -> provider via the catalog. Models that aren't in the
    // catalog are treated as "no provider known" and skipped (they may be
    // local / custom and don't need a BYOK key).
    const modelProvider = new Map<string, string>();
    for (const entry of llmCatalog) {
      if (entry.provider) modelProvider.set(entry.id, entry.provider);
    }

    // AC-1550 (epic AC-1537): gather (step, agent) pairs so the helper
    // can apply the effective-model fallback (``step.model OR agent.model``)
    // and surface provider mismatches arising from per-step overrides.
    // Pairs where neither side has a model id are tolerated and skipped.
    const stepAgentPairs: {
      step: { model?: string | null };
      agent: { model?: string | null };
    }[] = [];
    for (const node of nodes) {
      const step = node?.data?.step;
      if (!step || step.step_type !== 'run_agent') continue;
      const agentId = step.agent_id;
      if (!agentId) continue;
      const agent = agents.find((a) => a.agent_id === agentId);
      if (!agent) continue;
      stepAgentPairs.push({
        step: { model: (step as any).model ?? null },
        agent: { model: agent.model },
      });
    }
    return computeMissingKeyProviders(stepAgentPairs, modelProvider, configured);
  }, [llmByokMode, llmCatalog, apiKeysQuery.data, nodes, agents]);

  const hasMissingKeys = missingKeyProviders.length > 0;

  /**
   * Friendly capitalized provider name. Kept inline rather than imported
   * from settings so the banner stays self-contained.
   */
  const providerDisplayName = (p: string): string => {
    if (p === 'openai') return 'OpenAI';
    if (p === 'anthropic') return 'Anthropic';
    if (p === 'google') return 'Google';
    return p;
  };

  // Get the assistant step for chat interface
  const assistantStep = useMemo(() => {
    return nodes.find(node =>
      node.type === 'agent' &&
      node.data?.step?.intent === 'assistant'
    )?.data?.step;
  }, [nodes]);

  // Chat mode state for assistant conversations
  const [chatMode, setChatMode] = useState<'workflow' | 'chat'>('workflow');
  const [showChatInterface, setShowChatInterface] = useState(false);
  const [hasAutoSwitchedToChat, setHasAutoSwitchedToChat] = useState(false);


  // Save handler for the modal (both create and edit)
  const handleSaveStep = async (stepData: any) => {
    if (!workflowId) return;
    
    try {
      if (stepModalContext?.isEditing) {
        // Update existing step
        const node = nodes.find(n => n.id === stepModalContext.fromNodeId);
        if (node?.data?.step?.orchestration_step_id) {
          // Only send allowed fields for update
          const updateData = {
            description: stepData.description,
            label: stepData.label,
            input_message: stepData.input_message,
            agent_id: stepData.agent_id ? Number(stepData.agent_id) : undefined,
            // AC-1548 (epic AC-1537): thread step.model through the update
            // payload so the user's per-step override persists.
            model: stepData.model,
            condition: stepData.condition,
            show_output_message_items: stepData.show_output_message_items,
            position_x: stepData.position_x,
            position_y: stepData.position_y,
            on_true: stepData.on_true,
            on_false: stepData.on_false,
            execution_mode: stepData.execution_mode,
            fan_out: stepData.fan_out,
            feedback_loop_hints: stepData.feedback_loop_hints, // Include hints for updates
            intent: stepData.intent, // Include intent field for updates
            assistant_config: stepData.assistant_config, // Include assistant_config field for updates
            session_config: stepData.session_config, // Include session_config field for updates
            auto_prompt_output_mode: stepData.auto_prompt_output_mode, // Include auto_prompt fields for updates
            auto_prompt_populated_data: stepData.auto_prompt_populated_data,
            auto_prompt_model_id: stepData.auto_prompt_model_id,
            config: stepData.config, // Include step-specific config (e.g., for queue_read, queue_write)
          };
          console.log('[WorkflowEditor] Calling updateWorkflowStep with:', {
            step_id: node.data.step.orchestration_step_id,
            updateData
          });
          // AC-1406: Pessimistic step update — full-step edits (description,
          // agent_id, config, etc.) where the server is the source of truth
          // for many fields and a brief disabled state during the round-trip
          // is acceptable. The mutation hook invalidates the right RQ keys
          // on success so the canvas reflects the edit without a manual
          // `loadWorkflow` reload (regression-covers AC-1390 / AC-1391 /
          // AC-1394 stale-modal-data class of bugs).
          await updateStepMutation.mutateAsync({
            stepId: node.data.step.orchestration_step_id,
            data: updateData,
          });
        }
      } else if (stepModalContext?.fromNodeId) {
        // Create new step
        const sourceNode = nodes.find(n => n.id === stepModalContext.fromNodeId);
        const addNode = nodes.find(n => n.type === 'add' && n.data?.parentNodeId === stepModalContext.fromNodeId);
        
        // Center new step under source node for straight edge routing
        const nodeWidth = 288; // w-72 in Tailwind = 72 * 4 = 288px
        const newPositionX = sourceNode ? sourceNode.position.x : addNode?.position.x;

        const payload = {
          step_type: stepData.step_type,
          description: stepData.description,
          label: stepData.label,
          input_message: stepData.input_message,
          agent_id: stepData.agent_id ? Number(stepData.agent_id) : undefined,
          // AC-1548 (epic AC-1537): step-level model id; ``undefined`` is
          // fine (falls back to agent.model at runtime). Send through so
          // the persistence layer captures the user's choice.
          model: stepData.model,
          condition: stepData.condition,
          previous_step_id: sourceNode?.data?.step?.orchestration_step_id,
          position_x: newPositionX,
          position_y: addNode?.position.y,
          on_true: stepData.on_true,
          on_false: stepData.on_false,
          execution_mode: stepData.execution_mode,
          fan_out: stepData.fan_out,
          feedback_loop_hints: stepData.feedback_loop_hints, // Include hints for new steps
          intent: stepData.intent, // Include intent field
          assistant_config: stepData.assistant_config, // Include assistant_config field
          session_config: stepData.session_config, // Include session_config field
          auto_prompt_output_mode: stepData.auto_prompt_output_mode, // Include auto_prompt fields for new steps
          auto_prompt_populated_data: stepData.auto_prompt_populated_data,
          auto_prompt_model_id: stepData.auto_prompt_model_id,
          config: stepData.config, // Include step-specific config (e.g., for queue_read, queue_write)
        };
        console.log('[WorkflowEditor] Calling createWorkflowStep with:', {
          orchestration_id: workflowId,
          payload
        });
        // AC-1406: Step create routes through the mutation hook. The hook's
        // `onSuccess` invalidates `workflowSteps(id)` AND
        // `workflowExecution(id)` per TD section 2.6, so the canvas + the
        // execution panel both refresh atomically — no `loadWorkflow`
        // reload needed.
        await createStepMutation.mutateAsync(payload);
      }

      // AC-1406: No manual `loadWorkflow(workflowId)` here — the mutation
      // hooks above handle invalidation. The canvas re-renders because the
      // steps query has been marked stale; `WorkflowEditor`'s seed effect
      // rebuilds nodes/edges from the next refetch.
      setSelectedNode(null);
      setShowStepModal(false);
      setStepModalContext(null);

      // Show success notification
      toast.success(stepModalContext?.isEditing ? 'Step updated successfully' : 'Step created successfully');
    } catch (error: any) {
      if (error.response && error.response.data) {
        console.error('Backend validation error:', error.response.data);
        toast.error(`Failed to save step: ${error.response.data.detail || 'Validation error'}`);
      } else {
        console.error('Error saving workflow step:', error);
        toast.error('Failed to save step. Please try again.');
      }
    }
  };

  // Stable position key for start-node and add-nodes - only changes when positions change,
  // not on selection or other node property changes. This prevents edge re-renders on click.
  const startAddNodePositionKey = useMemo(() => {
    const startNode = nodes.find(n => n.id === 'start-node');
    const addNodes = nodes.filter(n => n.type === 'add');
    if (!startNode) return '';
    return `${Math.round(startNode.position.x)},${Math.round(startNode.position.y)}|${addNodes.map(n => `${n.id}:${Math.round(n.position.x)},${Math.round(n.position.y)}`).join('|')}`;
  }, [nodes]);

  // Update edge handles for start→add edges only when their positions change
  useEffect(() => {
    const startNode = nodes.find(n => n.id === 'start-node');
    if (!startNode) return;

    const addNodes = nodes.filter(n => n.type === 'add');
    if (addNodes.length === 0) return;

    const newEdges = edges.map((edge: Edge) => {
      // Only process start→add edges
      if (!edge.target.startsWith('add-node')) return edge;

      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return edge;

      const { sourceHandle, targetHandle } = selectEdgeHandles(sourceNode, targetNode);

      if (edge.sourceHandle !== sourceHandle || edge.targetHandle !== targetHandle) {
        return { ...edge, sourceHandle, targetHandle };
      }
      return edge;
    });

    const isChanged = newEdges.some((e, i) => e !== edges[i]);
    if (isChanged) {
      setEdges(newEdges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAddNodePositionKey]);

  // Add this useEffect before the modal rendering code
  useEffect(() => {
    if (showStepModal && stepModalContext?.fromNodeId) {
      const node = nodes.find(n => n.id === stepModalContext.fromNodeId);
      if (!node) {
        setShowStepModal(false);
        setStepModalContext(null);
      }
    }
  }, [showStepModal, stepModalContext, nodes]);

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const executingRef = useRef(false);

  // AC-465: Overlapping runs dialog state
  const [showOverlappingRunsDialog, setShowOverlappingRunsDialog] = useState(false);
  const [activeRuns, setActiveRuns] = useState<RunInstance[]>([]);
  const [executionLogs, setExecutionLogs] = useState<Array<{
    id: string;
    timestamp: string;
    level: 'info' | 'warning' | 'error';
    message: string;
    isStepOutput?: boolean;
    isFinalOutput?: boolean;
    stepOrder?: number;
    details?: any;
    rawData?: any;
    rawResult?: any;
  }>>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [isLogsFocused, setIsLogsFocused] = useState(false);
  const [showFinalOutputModal, setShowFinalOutputModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  // AC-1349: Edit agent deep-link from RunResultErrorPanel "underpowered model" callout.
  const [editAgentTarget, setEditAgentTarget] = useState<{
    agentId: number;
    focusField: 'model';
  } | null>(null);
  const [workflowSummary, setWorkflowSummary] = useState<string | null>(null);
  const [finalOutputData, setFinalOutputData] = useState<{
    runResult: RunResult;
    duration: string;
    totalSteps: number;
    timestamp: string;
  } | null>(null);
  const [showExecutionPanel, setShowExecutionPanel] = useState(false);
  const [currentRunInstanceId, setCurrentRunInstanceId] = useState<string | null>(null);
  const [waitingForUserInput, setWaitingForUserInput] = useState<{
    runResultId: string;
    message: string;
  } | null>(null);
  const [userInputValue, setUserInputValue] = useState('');
  const [showRunHistory, setShowRunHistory] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Track when user last viewed history (for badge count)
  // Persisted to localStorage so it survives page reloads
  const getLastViewedKey = useCallback(() => `history_last_viewed_orchestration_${workflowId}`, [workflowId]);
  const [historyLastViewed, setHistoryLastViewed] = useState<string | null>(() => {
    if (!workflowId) return null;
    return localStorage.getItem(`history_last_viewed_orchestration_${workflowId}`);
  });
  // Track which changes to highlight as "new" in the history panel
  // This is set to the previous lastViewed when opening the panel
  const [highlightChangesSince, setHighlightChangesSince] = useState<string | null>(null);

  // Update last viewed when workflowId changes
  useEffect(() => {
    if (workflowId) {
      const stored = localStorage.getItem(getLastViewedKey());
      setHistoryLastViewed(stored);
    }
  }, [workflowId, getLastViewedKey]);

  // Get recent change count for History button badge (only changes since last viewed)
  const historyKey = workflowId ? `orchestration_${workflowId}` : '';
  const allChanges = historyKey ? (entityHistory.get(historyKey) || []) : [];
  const recentChangeCount = useMemo(() => {
    if (!historyLastViewed) {
      // If never viewed, show all changes count
      return allChanges.length;
    }
    const lastViewedDate = new Date(historyLastViewed);
    return allChanges.filter(change => new Date(change.created_at) > lastViewedDate).length;
  }, [allChanges, historyLastViewed]);

  const [runHistory, setRunHistory] = useState<Array<{
    run_instance_id: string;
    status: string;
    created_at: string;
    completed_at?: string;
  }>>([]);
  const [selectedHistoryRun, setSelectedHistoryRun] = useState<string | null>(null);

  // Log Management Panel state
  const [showLogManagementPanel, setShowLogManagementPanel] = useState(false);

  // Schedule Modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<Schedule | null>(null);

  // WhatsApp notification toggle state (AC-1051)
  const [whatsappNotifyEnabled, setWhatsappNotifyEnabled] = useState(false);
  const [autoSummarizeEnabled, setAutoSummarizeEnabled] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);

  // Delivery channel setting state (AC-1816, epic AC-1813). Mirrors auto-summarize:
  // the current values come from the loaded workflow object; availableChannels is
  // fetched from GET /delivery/channels so only connected channels are offered.
  const [deliveryChannel, setDeliveryChannel] = useState<DeliveryChannel | null>(null);
  const [deliveryDestination, setDeliveryDestination] = useState<string | null>(null);
  const [deliveryCredentialId, setDeliveryCredentialId] = useState<number | null>(null);
  const [availableChannels, setAvailableChannels] = useState<ChannelAvailability[]>([]);

  // Output Display Modal state
  const [showOutputModal, setShowOutputModal] = useState(false);
  const [selectedRunResult, setSelectedRunResult] = useState<RunResult | null>(null);

  // Get session data from workflow store
  const currentExecution = useWorkflowStore(state =>
    workflowId ? state.executions.get(workflowId) : undefined
  );
  const sessionMessages = currentExecution?.sessionMessages || [];
  const sessionInfoMap = currentExecution?.sessionInfoMap || {};


  // Detect when we should show chat interface (for assistant workflows)
  useEffect(() => {
    if (hasAssistantSteps && currentRunInstanceId && workflowId) {
      // Check if we have an active execution with assistant steps
      const execution = useWorkflowStore.getState().executions.get(workflowId);
      if (execution && execution.runInstance) {
        // Check if any assistant step is active or waiting for input
        const hasActiveAssistantStep = execution.runResults?.some(result => 
          result.step_type === 'run_agent' && 
          result.final_output && 
          result.orchestration_step_id && 
          nodes.find(node => 
            node.data?.step?.orchestration_step_id === result.orchestration_step_id &&
            node.data?.step?.intent === 'assistant'
          )
        );
        
        const hasWaitingAssistantStep = waitingForUserInput && 
          nodes.find(node => 
            node.data?.step?.orchestration_step_id === waitingForUserInput.runResultId &&
            node.data?.step?.intent === 'assistant'
          );
        
        // Check if we're waiting for user input in a workflow that has assistant steps
        const hasUserPromptInAssistantWorkflow = waitingForUserInput && hasAssistantSteps;
        
        if (hasActiveAssistantStep || hasWaitingAssistantStep || hasUserPromptInAssistantWorkflow) {
          setShowChatInterface(true);
          setChatMode('chat');
          
          // Only automatically switch to chat view if we haven't done so already
          // This allows users to manually switch back to workflow view
          if (!hasAutoSwitchedToChat) {
            setViewMode('chat');
            setHasAutoSwitchedToChat(true);
          }
        }
      }
    } else if (!hasAssistantSteps || !currentRunInstanceId) {
      setShowChatInterface(false);
      setChatMode('workflow');
      setHasAutoSwitchedToChat(false); // Reset the flag when execution ends
      // Don't automatically switch back to workflow view - let user control this
    }
  }, [hasAssistantSteps, currentRunInstanceId, waitingForUserInput, workflowId, nodes, hasAutoSwitchedToChat]);

  // Sync currentRunInstanceId and waitingForUserInput from execution store
  // This ensures these state variables are set after page reload or login
  useEffect(() => {
    if (!workflowId) return;

    // Subscribe to store changes
    const unsubscribe = useWorkflowStore.subscribe((state) => {
      const execution = state.executions.get(workflowId);
      if (!execution) return;

      // Sync currentRunInstanceId — but NOT during active execution
      // (executeWorkflowImpl sets it explicitly via createRunInstance)
      if (!executingRef.current && execution.runInstance && execution.runInstance.run_instance_id) {
        const runInstanceId = execution.runInstance.run_instance_id.toString();
        setCurrentRunInstanceId(prev => {
          if (prev !== runInstanceId) {
            console.log('[DEBUG] Syncing currentRunInstanceId from store:', runInstanceId);
            return runInstanceId;
          }
          return prev;
        });
      }

      // Sync waitingForUserInput from execution state
      // But NOT if status is terminal (completed/failed/stopped) - user input no longer needed
      const isTerminal = execution.status === 'completed' || execution.status === 'failed' || execution.status === 'stopped';
      if (execution.waitingForUserInput && execution.userInputRunResultId && execution.userInputMessage && !isTerminal) {
        const runResultId = execution.userInputRunResultId;
        const message = execution.userInputMessage;
        setWaitingForUserInput(prev => {
          if (!prev || prev.runResultId !== runResultId) {
            console.log('[DEBUG] Syncing waitingForUserInput from store:', {
              runResultId,
              message
            });
            return {
              runResultId,
              message
            };
          }
          return prev;
        });
      } else if (isTerminal) {
        // Clear user input state when terminal status is reached
        // Check both local state AND store state to ensure clearing happens
        setWaitingForUserInput(prev => {
          if (prev !== null || execution.waitingForUserInput) {
            console.log('[DEBUG] Clearing waitingForUserInput due to terminal status:', execution.status);
            return null;
          }
          return prev;
        });
      }
    });

    // Also run immediately on mount
    const execution = useWorkflowStore.getState().executions.get(workflowId);
    if (execution) {
      if (execution.runInstance && execution.runInstance.run_instance_id) {
        const runInstanceId = execution.runInstance.run_instance_id.toString();
        console.log('[DEBUG] Initial sync currentRunInstanceId from store:', runInstanceId);
        setCurrentRunInstanceId(runInstanceId);
      }

      if (execution.waitingForUserInput && execution.userInputRunResultId && execution.userInputMessage) {
        console.log('[DEBUG] Initial sync waitingForUserInput from store:', {
          runResultId: execution.userInputRunResultId,
          message: execution.userInputMessage
        });
        setWaitingForUserInput({
          runResultId: execution.userInputRunResultId,
          message: execution.userInputMessage
        });
      }
    }

    return unsubscribe;
  }, [workflowId]);

  // Handle chat message sending for assistant conversations
  const handleChatMessageSend = useCallback(async (message: string) => {
    console.log('[DEBUG] handleChatMessageSend called with message:', message);
    console.log('[DEBUG] handleChatMessageSend - waitingForUserInput:', waitingForUserInput);
    console.log('[DEBUG] handleChatMessageSend - workflowId:', workflowId);
    console.log('[DEBUG] handleChatMessageSend - currentRunInstanceId:', currentRunInstanceId);

    if (!waitingForUserInput || !workflowId || !currentRunInstanceId) {
      console.error('[DEBUG] handleChatMessageSend - Aborting: missing required state');
      return;
    }

    try {
      console.log('[DEBUG] handleChatMessageSend - Calling handleSendMessage');
      // Use the existing handleSendMessage function for assistant conversations
      await handleSendMessage(message);

      // The workflow will continue automatically after user input
      // The chat interface will be updated through the polling mechanism
    } catch (error) {
      console.error('Failed to send chat message:', error);
    }
  }, [waitingForUserInput, workflowId, currentRunInstanceId, handleSendMessage]);

  // Load run history
  const loadRunHistory = useCallback(async () => {
    if (!workflowId) return;
    
    try {
      const runInstances = await apiService.getRunInstances(workflowId);
      const TERMINAL: ReadonlySet<string> = new Set(['completed', 'failed', 'stopped']);
      setRunHistory(runInstances.map(ri => ({
        run_instance_id: ri.run_instance_id,
        status: ri.status,
        created_at: ri.created_at,
        completed_at: TERMINAL.has(ri.status) ? ri.updated_at : undefined,
      })));
    } catch (error) {
      console.error('Failed to load run history:', error);
    }
  }, [workflowId]);

  // Sanitize data for safe rendering
  const sanitizeData = useCallback((data: any): any => {
    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
      return data;
    }
    if (data === null || data === undefined) {
      return 'N/A';
    }
    if (Array.isArray(data)) {
      return data.map(sanitizeData);
    }
    if (typeof data === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'object' && value !== null) {
          sanitized[key] = formatLogData(value);
        } else {
          sanitized[key] = value;
        }
      }
      return sanitized;
    }
    return String(data);
  }, []);

  const loadHistoricalLogs = useCallback(async (runInstanceId: string) => {
    if (!workflowId) return;
    
    try {
      console.log('Loading historical logs for run:', runInstanceId);
      
      // Clear current logs and show loading
      setExecutionLogs([{
        id: 'loading',
        timestamp: new Date().toISOString(),
        level: 'info',
        message: `Loading historical logs for run ${String(runInstanceId).slice(-8)}...`
      }]);
      
      const response = await apiService.getRunInstanceResults(runInstanceId, true);
      const runResults = response.results;
      console.log('Historical run results:', runResults);
      console.log('[DEBUG] loadHistoricalLogs: Session data in response:', {
        sessionMessagesCount: response.session_messages?.length || 0,
        sessionInfoMapKeys: Object.keys(response.session_info_map || {}).length
      });

      // Note: Session data is now handled by workflowStore.loadExecution()
      // No need to manually set sessionMessages/sessionInfoMap here

      // Create historical logs
      const historicalLogs = [];
      
      // Add start log with prominent notification
      historicalLogs.push({
        id: `historical-notification-${runInstanceId}`,
        timestamp: new Date().toISOString(),
        level: 'info' as const,
        message: `🔍 VIEWING HISTORICAL LOGS - Run ${String(runInstanceId).slice(-8)} (${runResults.length} steps)`,
        isFinalOutput: false,
        details: {
          isHistoricalNotification: true,
          runInstanceId: runInstanceId,
          totalSteps: runResults.length,
          originalStartTime: runResults[0]?.started_at,
        }
      });
      
      // Add start log
      historicalLogs.push({
        id: `historical-start-${runInstanceId}`,
        timestamp: runResults[0]?.started_at || new Date().toISOString(),
        level: 'info' as const,
        message: `📜 Historical Run ${String(runInstanceId).slice(-8)} - Started with ${runResults.length} steps`
      });
      
      // Add step output logs
      const stepOutputLogs = runResults.map((result, index) => ({
        id: `historical-step-${result.run_result_id}`,
        timestamp: result.completed_at || result.started_at,
        level: 'info' as const,
        message: `Step ${result.step_order} Output: ${result.final_output ? (result.final_output.length > 100 ? result.final_output.substring(0, 100) + '...' : result.final_output) : 'No output'}`,
        isStepOutput: true,
        stepOrder: result.step_order,
        details: sanitizeData({
          stepId: result.orchestration_step_id,
          runResultId: result.run_result_id,
          status: result.status,
          startedAt: result.started_at,
          completedAt: result.completed_at,
          duration: result.completed_at ?
            ((new Date(result.completed_at).getTime() - new Date(result.started_at).getTime()) / 1000).toFixed(2) + 's' :
            'N/A',
          inputData: result.input_data,
          finalOutput: result.final_output,
          errorMessage: result.error_message,
        }),
        rawData: sanitizeData(result),
        rawResult: result, // Attach the full backend object
      }));
      
      historicalLogs.push(...stepOutputLogs);
      
      // Add final completion log
      // Find the actual final step using robust selection criteria
      const finalResult = findFinalResult(runResults);
      const finalOutput = finalResult?.final_output;
      const runInstance = runHistory.find(ri => ri.run_instance_id === runInstanceId);
      
      historicalLogs.push({
        id: `historical-final-${runInstanceId}`,
        timestamp: finalResult?.completed_at || finalResult?.started_at || new Date().toISOString(),
        level: runInstance?.status === 'completed' ? 'info' as const : 'error' as const,
        message: runInstance?.status === 'completed'
          ? `📜 Historical Run ${String(runInstanceId).slice(-8)} - Completed successfully.${finalOutput ? ` Final Output: ${finalOutput.length > 100 ? finalOutput.substring(0, 100) + '...' : finalOutput}` : ''}`
          : `📜 Historical Run ${String(runInstanceId).slice(-8)} - Failed to complete`,
        isFinalOutput: runInstance?.status === 'completed',
        details: sanitizeData({
          totalSteps: runResults.length,
          totalDuration: runResults.length > 0 ?
            ((new Date(runResults[runResults.length - 1].completed_at || runResults[runResults.length - 1].started_at).getTime() -
              new Date(runResults[0].started_at).getTime()) / 1000).toFixed(2) + 's' : 'N/A',
          successfulSteps: runResults.filter(r => r.status === 'completed').length,
          failedSteps: runResults.filter(r => r.status === 'failed').length,
          finalOutput: finalOutput,
          runInstanceId: runInstanceId,
          isHistorical: true,
          stepId: finalResult?.orchestration_step_id || '', // Add stepId for fallback
          allStepResults: runResults.map(r => ({
            step: r.step_order,
            status: r.status,
            output: r.final_output,
            duration: r.completed_at ?
              ((new Date(r.completed_at).getTime() - new Date(r.started_at).getTime()) / 1000).toFixed(2) + 's' :
              'N/A'
          }))
        }),
        rawData: sanitizeData({ runResults, finalResult, runInstance }),
        rawResult: finalResult, // Attach the full backend object for final output log
      });
      
      setExecutionLogs(historicalLogs);
      setShowExecutionPanel(true);
      setSelectedHistoryRun(runInstanceId);
      
      // Ensure logs are visible by scrolling to execution panel if needed
      setTimeout(() => {
        const executionPanel = document.querySelector('[data-testid="execution-panel"]');
        if (executionPanel) {
          executionPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
      
    } catch (error) {
      console.error('Failed to load historical logs:', error);
      console.error('Error details:', error);
      setExecutionLogs([{
        id: 'error',
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Failed to load historical logs: ${error instanceof Error ? error.message : 'Unknown error'}`
      }]);
    }
  }, [workflowId, runHistory, sanitizeData]);

  // Load schedule for current orchestration
  const loadCurrentSchedule = useCallback(async () => {
    if (!workflowId) {
      setCurrentSchedule(null);
      return;
    }
    try {
      // getScheduleByWorkflow returns null when no schedule exists
      const schedule = await apiService.getScheduleByWorkflow(parseInt(workflowId, 10));
      setCurrentSchedule(schedule);
    } catch (err: any) {
      console.error('Error loading schedule:', err);
      setCurrentSchedule(null);
    }
  }, [workflowId]);

  // Load WhatsApp connection status and notification preference (AC-1051)
  useEffect(() => {
    if (!workflowId) return;
    // Check if WhatsApp is connected before showing the toggle
    apiService.whatsAppGetStatus()
      .then(status => {
        setWhatsappConnected(status.connected);
        if (!status.connected) return;
        // Load notification preference for this orchestration
        return apiService.whatsAppGetNotificationPreferences();
      })
      .then(prefs => {
        if (!prefs) return;
        const override = prefs.orchestration_overrides?.[workflowId];
        setWhatsappNotifyEnabled(override === true);
      })
      .catch(() => {
        setWhatsappConnected(false);
        setWhatsappNotifyEnabled(false);
      });
  }, [workflowId]);

  // Load delivery channel availability (AC-1816, epic AC-1813). Drives the
  // DeliveryChannelSetting selector: only `available` channels are offered.
  useEffect(() => {
    if (!workflowId) return;
    apiService.getDeliveryChannelAvailability()
      .then(channels => setAvailableChannels(channels))
      .catch(() => setAvailableChannels([]));
  }, [workflowId]);

  // Load run history when component mounts
  useEffect(() => {
    if (workflowId) {
      loadRunHistory();
    }
  }, [workflowId, loadRunHistory]);

  // Deep link: auto-open Final Output modal when ?run={id} is in the URL
  // (e.g. from WhatsApp notification). Two-phase approach:
  // Phase 1: detect ?run param, load historical logs, set pending flag
  // Phase 2: when executionLogs update and flag is set, open the modal
  const [pendingDeepLink, setPendingDeepLink] = useState(false);

  useEffect(() => {
    const runParam = searchParams.get('run');
    if (!runParam || runHistory.length === 0) return;

    const targetRun = runParam === 'latest'
      ? runHistory[0]
      : runHistory.find(r => String(r.run_instance_id) === runParam);

    if (targetRun) {
      setPendingDeepLink(true);
      loadHistoricalLogs(targetRun.run_instance_id);
    }
    searchParams.delete('run');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, runHistory, loadHistoricalLogs, setSearchParams]);

  // Phase 2: once historical logs are loaded, extract final output and open the modal
  useEffect(() => {
    if (!pendingDeepLink || executionLogs.length === 0) return;

    const finalLog = executionLogs.find(log => log.isFinalOutput && log.details?.finalOutput);
    if (!finalLog) return;

    // Logs are ready — clear the flag and open the modal
    setPendingDeepLink(false);

    let finalRunResult: any = null;
    if (finalLog.rawResult) {
      finalRunResult = finalLog.rawResult;
    } else if (finalLog.rawData) {
      const rawData = finalLog.rawData as any;
      if (rawData.finalResult) {
        finalRunResult = rawData.finalResult;
      } else if (rawData.runResults?.length > 0) {
        finalRunResult = findFinalResult(rawData.runResults) || null;
      }
    }

    if (!finalRunResult && finalLog.details?.finalOutput) {
      finalRunResult = {
        run_result_id: finalLog.details.runResultId || '',
        run_instance_id: '',
        orchestration_step_id: finalLog.details.stepId || '',
        step_order: finalLog.details.totalSteps || 0,
        status: 'completed',
        final_output: finalLog.details.finalOutput,
        started_at: finalLog.details.startedAt || finalLog.timestamp,
        completed_at: finalLog.details.completedAt || finalLog.timestamp,
        created_at: finalLog.timestamp,
      };
    }

    if (finalRunResult) {
      setFinalOutputData({
        runResult: finalRunResult,
        duration: finalLog.details?.totalDuration || 'N/A',
        totalSteps: finalLog.details?.totalSteps || 0,
        timestamp: finalLog.timestamp,
      });
      setShowFinalOutputModal(true);
    }
  }, [pendingDeepLink, executionLogs]);

  // Load schedule when workflowId changes
  useEffect(() => {
    loadCurrentSchedule();
  }, [loadCurrentSchedule]);

  // Load version history for badge count
  useEffect(() => {
    if (workflowId) {
      loadEntityHistory('orchestration', parseInt(String(workflowId), 10));
    }
  }, [workflowId, loadEntityHistory]);

  // Close run history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRunHistory && !(event.target as Element).closest('.run-history-dropdown')) {
        setShowRunHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRunHistory]);

  // AC-465: Check for overlapping runs before execution
  const checkForOverlappingRuns = useCallback(async (): Promise<RunInstance[]> => {
    if (!workflowId) return [];

    // Check if workflow has queue_read steps
    const hasQueueReadStep = nodes.some(node => node.data?.step?.step_type === 'queue_read');
    if (!hasQueueReadStep) return [];

    // Fetch run instances and check for active ones
    try {
      const runInstances = await apiService.getRunInstances(workflowId);
      const activeStatuses = ['pending', 'running', 'paused'];
      return runInstances.filter(ri => activeStatuses.includes(ri.status.toLowerCase()));
    } catch (error) {
      console.error('Failed to check for active runs:', error);
      return [];
    }
  }, [workflowId, nodes]);

  // AC-465: Handle overlapping runs dialog action
  const handleOverlappingRunsAction = useCallback(async (action: OverlappingRunAction) => {
    setShowOverlappingRunsDialog(false);
    setActiveRuns([]);

    if (action === 'cancel') {
      return;
    }

    // For both 'wait' and 'parallel', we proceed with execution
    // The backend handles queue item locking via FOR UPDATE SKIP LOCKED
    // 'wait' - The new run will automatically wait for queue items to become available
    // 'parallel' - Multiple runs can process different queue items concurrently
    executeWorkflowImpl();
  }, []);

  // AC-465: Extract the actual execution logic into a separate function
  const executeWorkflowImpl = useCallback(async () => {
    if (!workflowId) return;
    setIsExecuting(true);
    setShowExecutionPanel(true);
    setWaitingForUserInput(null);
    setUserInputValue('');
    setSelectedHistoryRun(null); // Exit historical log focus
    setShowExecutionPanel(true); // Ensure real-time log panel is visible
    setCurrentRunInstanceId(null);
    setExecutionLogs([]); // Clear logs immediately on new execution

    // Clear store state FIRST (before stopPolling which triggers subscription)
    const currentExecution = useWorkflowStore.getState().executions.get(workflowId);
    if (currentExecution) {
      useWorkflowStore.setState(state => {
        const executions = new Map(state.executions);
        executions.set(workflowId, {
          ...defaultExecution(),
          status: 'running',
          isRunning: true,
          lastUpdated: Date.now(),
        });
        return { executions };
      });
    }
    // Stop polling AFTER clearing store state
    useWorkflowStore.getState().stopPolling(workflowId);
    
    try {
      // Step 1: Create run instance FIRST to get the correct ID immediately
      console.log('[DEBUG] Creating run instance...');
      const createdRunInstance = await apiService.createRunInstance(workflowId);
      const runInstanceId = typeof createdRunInstance.run_instance_id === 'string'
        ? parseInt(createdRunInstance.run_instance_id, 10)
        : createdRunInstance.run_instance_id;
      console.log('[DEBUG] Created run instance:', runInstanceId);

      // Show the start log immediately with the correct ID
      setExecutionLogs([{
        id: `start-${runInstanceId}`,
        timestamp: new Date().toISOString(),
        level: 'info' as const,
        message: `Workflow execution started - Run ID: ${String(runInstanceId).slice(-8)}`
      }]);

      // Step 2: Fire the actual execution non-blocking with the pre-created ID
      apiService.runWorkflow(workflowId, String(runInstanceId))
        .catch((error) => {
          console.error('[DEBUG] runWorkflow failed:', error);
          const apiDetail = error?.response?.data?.detail;
          const statusCode = error?.response?.status;
          const cleanDetail = apiDetail?.replace(/^Internal server error:\s*/i, '');
          const userMessage = cleanDetail || (error instanceof Error ? error.message : 'Unknown error');

          const errorLog = {
            id: (Date.now() + 1).toString(),
            timestamp: new Date().toISOString(),
            level: 'error' as const,
            message: `Workflow execution failed: ${userMessage}`,
            details: statusCode ? { status: statusCode, detail: apiDetail || 'No details available' } : undefined,
          };
          setExecutionLogs(prev => [...prev, errorLog]);
          setIsExecuting(false); executingRef.current = false;
        });

      const mostRecentRunInstance = createdRunInstance;
      console.log('[DEBUG] Got run_instance_id:', runInstanceId);
      setCurrentRunInstanceId(runInstanceId.toString());

      // Step 3: Update the store with the new execution state
      useWorkflowStore.setState(state => {
        const executions = new Map(state.executions);
        const prev = executions.get(workflowId) || defaultExecution();
        executions.set(workflowId, {
          ...prev,
          runInstance: mostRecentRunInstance,
          runResults: [],
          status: mostRecentRunInstance.status, // Use actual status from backend, not hardcoded 'running'
          isRunning: mostRecentRunInstance.status === 'running',
          isPaused: mostRecentRunInstance.status === 'paused',
          lastUpdated: Date.now(),
        });
        return { executions };
      });

      // Step 4: Immediately start polling for this run instance
      // Polling will now run in parallel with the backend execution, fetching incremental results
      console.log('[DEBUG] Starting polling immediately for run_instance_id:', runInstanceId);
      useWorkflowStore.getState().startPolling(workflowId, runInstanceId.toString());

      // Note: We don't check for user prompts here anymore - polling will handle status changes
      // and the store's onStatusChange callback will handle paused status
    } catch (error) {
      const errorLog = {
        id: (Date.now() + 1).toString(),
        timestamp: new Date().toISOString(),
        level: 'error' as const,
        message: `Workflow execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
      setExecutionLogs(prev => [...prev, errorLog]);
      setCurrentRunInstanceId(null);
      setIsExecuting(false); executingRef.current = false;

      // Clear the runInstance from store to prevent stale run ID from being displayed
      useWorkflowStore.setState(state => {
        const executions = new Map(state.executions);
        const execution = executions.get(workflowId);
        if (execution) {
          executions.set(workflowId, {
            ...execution,
            runInstance: null,
            status: 'stopped',
            isRunning: false,
            lastUpdated: Date.now(),
          });
        }
        return { executions };
      });
    }
  }, [workflowId]);

  // Helper function to poll for workflow completion
  const pollForCompletion = useCallback(async (runInstanceId: string) => {
    const maxAttempts = 15;
    let attempt = 0;
    
    while (attempt < maxAttempts) {
      try {
        const runInstances = await apiService.getRunInstances(workflowId!);
        const currentInstance = runInstances.find(ri => ri.run_instance_id === runInstanceId);
        
        if (currentInstance?.status === 'completed') {
          // Fetch and log step outputs
          try {
            const response = await apiService.getRunInstanceResults(runInstanceId);
            const runResults = response.results;
            const stepOutputLogs = runResults.map((result, index) => ({
              id: `step-output-${result.run_result_id}`,
              timestamp: result.completed_at || result.started_at,
              level: 'info' as const,
              message: `Step ${result.step_order} Output: ${result.final_output ? (result.final_output.length > 100 ? result.final_output.substring(0, 100) + '...' : result.final_output) : 'No output'}`,
              isStepOutput: true,
              stepOrder: result.step_order,
              details: sanitizeData({
                stepId: result.orchestration_step_id,
                runResultId: result.run_result_id,
                status: result.status,
                startedAt: result.started_at,
                completedAt: result.completed_at,
                duration: result.completed_at ?
                  ((new Date(result.completed_at).getTime() - new Date(result.started_at).getTime()) / 1000).toFixed(2) + 's' :
                  'N/A',
                inputData: result.input_data,
                finalOutput: result.final_output,
                errorMessage: result.error_message,
              }),
              rawData: sanitizeData(result),
            }));

            // Add step output logs
            setExecutionLogs(prev => [...prev, ...stepOutputLogs]);

            // Add final completion log with highlighting
            // Find the actual final step using robust selection criteria
            const finalResult = findFinalResult(runResults);
            const finalOutput = finalResult?.final_output;

            const successLog = {
              id: (Date.now() + 1).toString(),
              timestamp: new Date().toISOString(),
              level: 'info' as const,
              message: `Workflow execution completed successfully.${finalOutput ? ` Final Output: ${finalOutput.length > 100 ? finalOutput.substring(0, 100) + '...' : finalOutput}` : ''}`,
              isFinalOutput: true,
              details: {
                totalSteps: runResults.length,
                totalDuration: runResults.length > 0 ?
                  ((new Date(runResults[runResults.length - 1].completed_at || runResults[runResults.length - 1].started_at).getTime() -
                    new Date(runResults[0].started_at).getTime()) / 1000).toFixed(2) + 's' : 'N/A',
                successfulSteps: runResults.filter(r => r.status === 'completed').length,
                failedSteps: runResults.filter(r => r.status === 'failed').length,
                finalOutput: finalOutput,
                allStepResults: runResults.map(r => ({
                  step: r.step_order,
                  status: r.status,
                  output: r.final_output,
                  duration: r.completed_at ?
                    ((new Date(r.completed_at).getTime() - new Date(r.started_at).getTime()) / 1000).toFixed(2) + 's' :
                    'N/A'
                }))
              },
              rawData: sanitizeData({ runResults, finalResult }),
              rawResult: finalResult, // Add rawResult for Quick View button to use
            };
            setExecutionLogs(prev => [...prev, successLog]);

            // Show final output modal
            if (finalOutput && finalResult) {
              setFinalOutputData({
                runResult: finalResult,
                duration: runResults.length > 0 ?
                  ((new Date(runResults[runResults.length - 1].completed_at || runResults[runResults.length - 1].started_at).getTime() -
                    new Date(runResults[0].started_at).getTime()) / 1000).toFixed(2) + 's' : 'N/A',
                totalSteps: runResults.length,
                timestamp: new Date().toISOString(),
              });
              setShowFinalOutputModal(true);
            }

            // Advance tutorial if on step 15 (Run Workflow)
            if (tutorialStatus === 'in_progress' && currentStep === 15) {
              advanceStep();
            }
          } catch (error) {
            console.error('Failed to fetch step outputs:', error);
            const successLog = {
              id: (Date.now() + 1).toString(),
              timestamp: new Date().toISOString(),
              level: 'info' as const,
              message: 'Workflow execution completed successfully.'
            };
            setExecutionLogs(prev => [...prev, successLog]);

            // Advance tutorial if on step 15 (Run Workflow) - even if fetch step outputs failed
            if (tutorialStatus === 'in_progress' && currentStep === 15) {
              advanceStep();
            }
          }
          
          setCurrentRunInstanceId(null);
          setIsExecuting(false); executingRef.current = false;
          return;
        } else if (currentInstance?.status === 'failed') {
          const errorLog = {
            id: (Date.now() + 1).toString(),
            timestamp: new Date().toISOString(),
            level: 'error' as const,
            message: 'Workflow execution failed.'
          };
          setExecutionLogs(prev => [...prev, errorLog]);
          setCurrentRunInstanceId(null);
          setIsExecuting(false); executingRef.current = false;
          return;
        }
        
        attempt++;
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
      } catch (error) {
        console.error('Error polling for completion:', error);
        break;
      }
    }
    
    // Timeout
    const timeoutLog = {
      id: (Date.now() + 1).toString(),
      timestamp: new Date().toISOString(),
      level: 'warning' as const,
      message: 'Workflow execution timed out while polling for completion.'
    };
    setExecutionLogs(prev => [...prev, timeoutLog]);
    setIsExecuting(false); executingRef.current = false;
  }, [workflowId, sanitizeData]);

  // AC-465: Main entry point for workflow execution
  // Checks for overlapping runs before executing
  const handleExecuteWorkflow = useCallback(async () => {
    if (!workflowId) return;

    // Flip to the executing UI immediately so the user gets feedback the
    // moment they click, instead of waiting for checkForOverlappingRuns +
    // create-run + setState (which can take a couple seconds combined).
    setIsExecuting(true);
    executingRef.current = true;
    setShowExecutionPanel(true);

    // Check for overlapping runs for queue-consuming orchestrations
    const overlappingRuns = await checkForOverlappingRuns();
    if (overlappingRuns.length > 0) {
      // Roll back the optimistic UI flip; the dialog handler will re-trigger
      // executeWorkflowImpl which sets isExecuting again on its own.
      setIsExecuting(false);
      executingRef.current = false;
      setActiveRuns(overlappingRuns);
      setShowOverlappingRunsDialog(true);
      return;
    }

    // No overlapping runs, proceed with execution
    executeWorkflowImpl();
  }, [workflowId, checkForOverlappingRuns, executeWorkflowImpl]);

  // Add onStartExecution callback to first step nodes (initiator nodes without previous_step_id)
  // Use a ref to store the handler to avoid infinite loops from function reference changes
  const handleExecuteWorkflowRef = useRef(handleExecuteWorkflow);
  handleExecuteWorkflowRef.current = handleExecuteWorkflow;

  useEffect(() => {
    const initiatorTypes = ['userPrompt', 'autoPrompt', 'queueRead'];
    const firstStepsWithoutCallback = nodes.filter(node =>
      initiatorTypes.includes(node.type || '') &&
      !node.data?.step?.previous_step_id &&
      typeof node.data?.onStartExecution !== 'function'
    );

    if (firstStepsWithoutCallback.length > 0) {
      const updatedNodes = nodes.map(node => {
        if (
          initiatorTypes.includes(node.type || '') &&
          !node.data?.step?.previous_step_id &&
          typeof node.data?.onStartExecution !== 'function'
        ) {
          return {
            ...node,
            data: {
              ...node.data,
              onStartExecution: () => handleExecuteWorkflowRef.current()
            }
          };
        }
        return node;
      });
      setNodes(updatedNodes);
    }
  }, [nodes, setNodes]);

  const handleStopExecution = useCallback(async () => {
    if (!workflowId) {
      console.error('[DEBUG] handleStopExecution: No workflowId available');
      return;
    }

    console.log('[DEBUG] handleStopExecution: Stopping orchestration:', workflowId);

    try {
      // Call the store's stopWorkflow method which will:
      // 1. Call the backend /stop endpoint
      // 2. Update the status to 'stopped' in the backend
      // 3. Stop polling
      // 4. Update local state
      await useWorkflowStore.getState().stopWorkflow(workflowId);

      // Update local UI state
      setIsExecuting(false); executingRef.current = false;
      setWaitingForUserInput(null);
      setCurrentRunInstanceId(null);
      setUserInputValue('');

      const stopLog = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        level: 'warning' as const,
        message: 'Workflow execution stopped by user'
      };
      setExecutionLogs(prev => [...prev, stopLog]);

      console.log('[DEBUG] handleStopExecution: Successfully stopped orchestration');
    } catch (error) {
      console.error('[DEBUG] handleStopExecution: Failed to stop workflow:', error);

      const errorLog = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        level: 'error' as const,
        message: `Failed to stop workflow: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
      setExecutionLogs(prev => [...prev, errorLog]);
    }
  }, [workflowId]);

  // Handle user input submission following the test pattern
  const handleSubmitUserInput = useCallback(async () => {
    console.log('[DEBUG] handleSubmitUserInput called');
    console.log('[DEBUG] handleSubmitUserInput guards:', {
      waitingForUserInput,
      userInputValue,
      workflowId,
      currentRunInstanceId
    });
    if (!waitingForUserInput || !userInputValue.trim() || !workflowId || !currentRunInstanceId) return;
    
    console.log('[DEBUG] handleSubmitUserInput: All guards passed, entering try block');
    
    try {
      console.log('[DEBUG] handleSubmitUserInput: Creating input log');
      const inputLog = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        level: 'info' as const,
        message: `User input provided: ${userInputValue}`
      };
      setExecutionLogs(prev => [...prev, inputLog]);
      
      console.log('[DEBUG] handleSubmitUserInput: About to call setUserPromptResponse with:', {
        runResultId: waitingForUserInput.runResultId,
        userInput: userInputValue
      });
      await apiService.setUserPromptResponse(waitingForUserInput.runResultId, userInputValue);
      console.log('[DEBUG] handleSubmitUserInput: setUserPromptResponse completed successfully');

      // Clear user input state immediately after submission (both local and store)
      setWaitingForUserInput(null);
      useWorkflowStore.getState().clearUserInputRequired(workflowId);
      setUserInputValue('');
      setIsExecuting(true);
      
      console.log('[DEBUG] handleSubmitUserInput: Creating continue log');
      const continueLog = {
        id: (Date.now() + 1).toString(),
        timestamp: new Date().toISOString(),
        level: 'info' as const,
        message: 'Resuming workflow execution...'
      };
      setExecutionLogs(prev => [...prev, continueLog]);
      
      // After submitting user input, the backend should automatically continue execution
      // We need to call runWorkflow again to trigger the backend to continue
      console.log('[DEBUG] handleSubmitUserInput: User input submitted, calling runWorkflow to continue execution');
      
      // Call runWorkflow with a shorter timeout to prevent hanging
      const runWorkflowWithTimeout = async () => {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('runWorkflow timed out after 10 seconds'));
          }, 10000); // 10 second timeout (shorter than before)
          
          apiService.runWorkflow(workflowId, currentRunInstanceId)
            .then((result) => {
              clearTimeout(timeout);
              resolve(result);
            })
            .catch((error) => {
              clearTimeout(timeout);
              reject(error);
            });
        });
      };
      
      console.log('[DEBUG] handleSubmitUserInput: Starting runWorkflow with 10-second timeout');
      try {
        await runWorkflowWithTimeout();
        console.log('[DEBUG] handleSubmitUserInput: runWorkflow completed successfully');
      } catch (error) {
        console.warn('[DEBUG] handleSubmitUserInput: runWorkflow timed out or failed, but continuing with polling:', error);
        // Continue with polling even if runWorkflow fails
      }
      
      // Immediately start polling for the current run instance
      if (currentRunInstanceId) {
        console.log('[DEBUG] About to call startPolling with', workflowId, currentRunInstanceId);
        useWorkflowStore.getState().startPolling(workflowId, currentRunInstanceId);
        console.log('[DEBUG] startPolling call completed');
      } else {
        console.warn('[DEBUG] No current run instance ID available, skipping polling start');
      }
      
      // Add a log to show the current store state after starting polling
      setTimeout(() => {
        const currentExecution = useWorkflowStore.getState().executions.get(workflowId);
        console.log('[DEBUG] Store state 1 second after startPolling:', {
          workflowId,
          hasExecution: !!currentExecution,
          isPolling: currentExecution?.isPolling,
          runResultsCount: currentExecution?.runResults.length,
          status: currentExecution?.status
        });
      }, 1000);
      
    } catch (error) {
      console.error('[DEBUG] handleSubmitUserInput: Error caught:', error);
      const errorLog = {
        id: (Date.now() + 1).toString(),
        timestamp: new Date().toISOString(),
        level: 'error' as const,
        message: `Failed to submit user input: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
      setExecutionLogs(prev => [...prev, errorLog]);
      setIsExecuting(false); executingRef.current = false;
    }
  }, [waitingForUserInput, userInputValue, workflowId, currentRunInstanceId]);

  // Toggle log expansion
  const toggleLogExpansion = useCallback((logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  }, []);

  // Format data for display
  const formatLogData = (data: any): string => {
    if (typeof data === 'string') return data;
    if (typeof data === 'number' || typeof data === 'boolean') return String(data);
    if (data === null || data === undefined) return 'N/A';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const handleViewFullOutput = (runResult: RunResult) => {
    setSelectedRunResult(runResult);
    setShowOutputModal(true);
  };

  const handleNavigateToStep = (runResult: RunResult) => {
    setSelectedRunResult(runResult);
    // Also update finalOutputData when navigating within the Final Output modal
    if (showFinalOutputModal && finalOutputData) {
      setFinalOutputData({ ...finalOutputData, runResult });
    }
  };

  // AC-1349: Open AgentFormModal from the RunResultErrorPanel "underpowered model"
  // callout. focusField='model' tells the modal to expand Advanced Settings and
  // scroll the model dropdown into view.
  const handleEditAgent = (agentId: number, focusField: 'model') => {
    setEditAgentTarget({ agentId, focusField });
  };

  const handleEditAgentSubmit = async (formData: CreateAgentForm) => {
    if (!editAgentTarget) return;
    try {
      await apiService.updateAgent(String(editAgentTarget.agentId), formData);
      if (projectId) {
        // AC-1412 / AC-1377 P5.1: refresh agents via the RQ cache instead
        // of the legacy loadAgents() store method. The active useAgents()
        // subscription will refetch on invalidation.
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectAgents(projectId),
        });
      }
      setEditAgentTarget(null);
    } catch (err) {
      toast.error('Failed to update agent');
      console.error('Error updating agent:', err);
    }
  };

  // Navigate to Enhanced Execution Dashboard
  const handleNavigateToExecutionDashboard = useCallback(() => {
    if (projectId && workflowId) {
      navigate(`/projects/${projectId}/workflows/${workflowId}/execute`);
    }
  }, [navigate, projectId, workflowId]);

  // --- FEEDBACK LOOP EDGE INJECTION ---
  // Stable key for feedback loop computation - only changes when positions or step data change,
  // NOT on selection changes. This prevents unnecessary edge re-renders that can cause
  // edge handle positions to shift when clicking nodes.
  const feedbackLoopKey = useMemo(() => {
    return nodes
      .filter(n => n.type === 'conditional' || n.type === 'agent' || n.type === 'userPrompt')
      .map(n => {
        const s = n.data?.step;
        return `${n.id}|${Math.round(n.position.x)}|${Math.round(n.position.y)}|${s?.step_type}|${s?.on_true?.action}|${s?.on_true?.next_step_id}|${s?.on_false?.action}|${s?.on_false?.next_step_id}|${s?.intent}|${s?.orchestration_step_id}`;
      })
      .join(',');
  }, [nodes]);

  // Compute auto-generated edges and virtual nodes for conditional branches and assistant loops
  const { autoEdges, autoEndNodes, edgeColorOverrides } = useMemo(() => {
    const autoEdges: Edge[] = [];
    const autoEndNodes: Node[] = [];
    const edgeColorOverrides = new Map<string, { stroke: string; label: string; labelStyle: Record<string, unknown>; labelBgStyle: Record<string, unknown> }>();

    nodes.forEach((node) => {
      // Handle conditional steps
      if (node.type === 'conditional' && node.data?.step) {
        const step = node.data.step;
        const stepId = step.orchestration_step_id?.toString();

        // --- on_true processing ---
        if (step.on_true?.action === 'feedback_loop' && step.on_true.next_step_id) {
          const targetNode = nodes.find(n => n.data?.step?.orchestration_step_id?.toString() === step.on_true.next_step_id?.toString());
          if (targetNode) {
            const fbEdgeId = `feedback-loop-ontrue-${node.id}-${targetNode.id}`;
            const layoutHandles = layoutEdgeHandlesRef.current.get(fbEdgeId);
            const { sourceHandle, targetHandle } = layoutHandles || selectFeedbackHandles(node, targetNode);
            const feedbackLabel = step.on_true.max_attempts
              ? `Feedback (${step.on_true.max_attempts} max)`
              : 'Feedback';
            autoEdges.push({
              id: fbEdgeId,
              source: node.id,
              target: targetNode.id,
              sourceHandle,
              targetHandle,
              type: 'feedbackLoop',
              animated: true,
              style: { stroke: '#10b981', strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#10b981' },
              data: { isFeedbackLoop: true, label: feedbackLabel, conditionType: 'true' },
            });
          }
        } else if (step.on_true?.action === 'exit') {
          // Create virtual EndNode and dashed exit edge
          const endNodeId = `end-${node.id}-true`;
          // Use layout-computed position if available, otherwise default offset
          const layoutPos = layoutEndNodePositionsRef.current.get(endNodeId);
          const endPos = layoutPos || { x: node.position.x + 220, y: node.position.y + 220 };
          autoEndNodes.push({
            id: endNodeId,
            type: 'end',
            position: endPos,
            data: { label: 'Exit', conditionType: 'true' },
            draggable: true,
            selectable: false,
          });
          const exitEdgeId = `exit-edge-ontrue-${node.id}`;
          const layoutHandles = layoutEdgeHandlesRef.current.get(exitEdgeId);
          const { sourceHandle, targetHandle } = layoutHandles || selectExitHandles(node, endPos);
          autoEdges.push({
            id: exitEdgeId,
            source: node.id,
            target: endNodeId,
            sourceHandle,
            targetHandle,
            type: 'feedbackLoop',
            animated: false,
            style: { stroke: '#ef4444', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#ef4444' },
            data: { label: 'True', isExit: true, conditionType: 'true' },
          });
        } else if (step.on_true?.action === 'continue' && stepId) {
          // Find existing edge from this conditional to next step and apply green color
          const nextNode = nodes.find(n =>
            n.data?.step?.previous_step_id?.toString() === stepId
          );
          if (nextNode) {
            const nextStepId = nextNode.data?.step?.orchestration_step_id?.toString();
            const edgeId = `e-${stepId}-${nextStepId}`;
            edgeColorOverrides.set(edgeId, {
              stroke: '#10b981',
              label: 'True',
              labelStyle: { fill: '#10b981', fontWeight: 700, fontSize: 11 },
              labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
            });
          }
        }

        // --- on_false processing ---
        if (step.on_false?.action === 'feedback_loop' && step.on_false.next_step_id) {
          const targetNode = nodes.find(n => n.data?.step?.orchestration_step_id?.toString() === step.on_false.next_step_id?.toString());
          if (targetNode) {
            const fbEdgeId = `feedback-loop-onfalse-${node.id}-${targetNode.id}`;
            const layoutHandles = layoutEdgeHandlesRef.current.get(fbEdgeId);
            const { sourceHandle, targetHandle } = layoutHandles || selectFeedbackHandles(node, targetNode);
            const feedbackLabel = step.on_false.max_attempts
              ? `Feedback (${step.on_false.max_attempts} max)`
              : 'Feedback';
            autoEdges.push({
              id: fbEdgeId,
              source: node.id,
              target: targetNode.id,
              sourceHandle,
              targetHandle,
              type: 'feedbackLoop',
              animated: true,
              style: { stroke: '#ef4444', strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#ef4444' },
              data: { isFeedbackLoop: true, label: feedbackLabel, conditionType: 'false' },
            });
          }
        } else if (step.on_false?.action === 'exit') {
          // Create virtual EndNode and dashed exit edge
          const endNodeId = `end-${node.id}-false`;
          // Use layout-computed position if available, otherwise default offset
          const layoutPos = layoutEndNodePositionsRef.current.get(endNodeId);
          const xOffset = step.on_true?.action === 'exit' ? -100 : 220;
          const endPos = layoutPos || { x: node.position.x + xOffset, y: node.position.y + 220 };
          autoEndNodes.push({
            id: endNodeId,
            type: 'end',
            position: endPos,
            data: { label: 'Exit', conditionType: 'false' },
            draggable: true,
            selectable: false,
          });
          const exitEdgeId = `exit-edge-onfalse-${node.id}`;
          const layoutHandles = layoutEdgeHandlesRef.current.get(exitEdgeId);
          const { sourceHandle, targetHandle } = layoutHandles || selectExitHandles(node, endPos);
          autoEdges.push({
            id: exitEdgeId,
            source: node.id,
            target: endNodeId,
            sourceHandle,
            targetHandle,
            type: 'feedbackLoop',
            animated: false,
            style: { stroke: '#ef4444', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#ef4444' },
            data: { label: 'False', isExit: true, conditionType: 'false' },
          });
        } else if (step.on_false?.action === 'continue' && stepId) {
          // Find existing edge from this conditional to next step and apply amber color
          const nextNode = nodes.find(n =>
            n.data?.step?.previous_step_id?.toString() === stepId
          );
          if (nextNode) {
            const nextStepId = nextNode.data?.step?.orchestration_step_id?.toString();
            const edgeId = `e-${stepId}-${nextStepId}`;
            // Only set if not already overridden by on_true (on_true takes precedence for same edge)
            if (!edgeColorOverrides.has(edgeId)) {
              edgeColorOverrides.set(edgeId, {
                stroke: '#f59e0b',
                label: 'False',
                labelStyle: { fill: '#f59e0b', fontWeight: 700, fontSize: 11 },
                labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
              });
            }
          }
        }
      }

      // Handle assistant steps with auto_loop enabled
      if (node.type === 'agent' && node.data?.step?.intent === 'assistant') {
        const assistantStep = node.data.step;

        // Find the nearest user_prompt step above or to the left
        let targetNode: Node | undefined = undefined;
        let bestDistance = Infinity;

        nodes.forEach((candidateNode) => {
          if (candidateNode.type === 'userPrompt' && candidateNode.data?.step) {
            const dx = node.position.x - candidateNode.position.x;
            const dy = node.position.y - candidateNode.position.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < bestDistance && (dy > 0 || (dy === 0 && dx > 0))) {
              bestDistance = distance;
              targetNode = candidateNode;
            }
          }
        });

        if (targetNode) {
          const sourceX = node.position.x;
          const sourceY = node.position.y;
          const targetX = (targetNode as Node).position.x;
          const targetY = (targetNode as Node).position.y;

          let sourceHandle = 'feedback-out-left';
          let targetHandle = 'feedback-in-right';

          const dx = targetX - sourceX;
          const dy = targetY - sourceY;

          if (Math.abs(dx) > Math.abs(dy)) {
            if (dx > 0) {
              sourceHandle = 'feedback-out-right';
              targetHandle = 'feedback-in-left';
            } else {
              sourceHandle = 'feedback-out-left';
              targetHandle = 'feedback-in-right';
            }
          } else {
            if (dy > 0) {
              sourceHandle = 'feedback-out-top';
              targetHandle = 'feedback-in-bottom';
            } else {
              sourceHandle = 'feedback-out-bottom';
              targetHandle = 'feedback-in-top';
            }
          }
          autoEdges.push({
            id: `assistant-auto-loop-${node.id}-${(targetNode as Node).id}`,
            source: node.id,
            target: (targetNode as Node).id,
            sourceHandle,
            targetHandle,
            type: 'feedbackLoop',
            animated: true,
            style: { stroke: '#8b5cf6', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#8b5cf6' },
            data: { isFeedbackLoop: true, label: 'Assistant Loop', conditionType: 'assistant' },
          });
        }
      }
    });
    return { autoEdges, autoEndNodes, edgeColorOverrides };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackLoopKey]);

  // Keep refs in sync for layout handlers that need current auto-generated data
  autoEndNodesRef.current = autoEndNodes;
  autoEdgesRef.current = autoEdges;

  // Sync managedEndNodes when topology changes (new/removed EndNodes from autoEndNodes).
  // Preserves user-dragged positions for existing EndNodes; uses layout ref for new ones.
  useEffect(() => {
    setManagedEndNodes(prev => {
      return autoEndNodes.map(n => {
        const existing = prev.find(e => e.id === n.id);
        if (existing) return { ...n, position: existing.position, ...(existing.width != null && { width: existing.width, height: existing.height }) };
        const layoutPos = layoutEndNodePositionsRef.current.get(n.id);
        return layoutPos ? { ...n, position: layoutPos } : n;
      });
    });
  }, [autoEndNodes]);

  // Combine real nodes with managed EndNodes.
  // managedEndNodes is proper React state updated via applyNodeChanges,
  // so React Flow can track EndNode positions/dimensions during drag.
  const allNodes = useMemo(() => {
    return [...nodes, ...managedEndNodes];
  }, [nodes, managedEndNodes]);

  // Combine normal edges (with color overrides) and auto-generated edges
  const allEdges = useMemo(() => {
    const coloredEdges = edges.map(edge => {
      const override = edgeColorOverrides.get(edge.id);
      if (override) {
        return {
          ...edge,
          style: { ...edge.style, stroke: override.stroke, strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: override.stroke },
          label: override.label,
          labelStyle: override.labelStyle,
          labelBgStyle: override.labelBgStyle,
          labelBgPadding: [4, 8] as [number, number],
          labelBgBorderRadius: 8,
        };
      }
      return edge;
    });
    return [...coloredEdges, ...autoEdges];
  }, [edges, autoEdges, edgeColorOverrides]);

  const execution = useWorkflowStore(state => workflowId ? state.executions.get(workflowId) : null);
  const runResults = execution?.runResults || [];

  // Debug logging for UI rendering — fires on every render. Gate behind a
  // localStorage flag so it can be flipped on for diagnosis without flooding
  // the console during normal use. Set localStorage.debugWorkflowRender = '1'
  // in DevTools to enable.
  if (
    import.meta.env.DEV &&
    execution &&
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('debugWorkflowRender') === '1'
  ) {
    console.log('[DEBUG] WorkflowEditor render - execution:', {
      workflowId,
      hasExecution: !!execution,
      runResultsCount: runResults.length,
      executionStatus: execution?.status,
      isRunning: execution?.isRunning,
      isPaused: execution?.isPaused,
      isPolling: execution?.isPolling,
      waitingForUserInput: execution?.waitingForUserInput,
      userInputRunResultId: execution?.userInputRunResultId,
      userInputMessage: execution?.userInputMessage,
      lastUpdated: execution?.lastUpdated
    });

    if (runResults.length > 0) {
      console.log('[DEBUG] WorkflowEditor render - runResults:', runResults);
    }
  }

  // Update executionLogs when new runResults are added to the store
  useEffect(() => {
    // Only process logs if there's an execution
    if (!execution) {
      return;
    }

    // While a new execution is starting but hasn't produced results yet,
    // don't overwrite the cleared logs with stale data
    if (isExecuting && runResults.length === 0 && !currentRunInstanceId) {
      return;
    }

    // Clear logs when runResults is empty (new execution starting)
    if (runResults.length === 0) {
      // Add initial start log when execution begins
      if (currentRunInstanceId && !selectedHistoryRun) {
        setExecutionLogs([{
          id: `start-${currentRunInstanceId}`,
          timestamp: new Date().toISOString(),
          level: 'info' as const,
          message: `Workflow execution started - Run ID: ${String(currentRunInstanceId).slice(-8)}`
        }]);
      } else {
        setExecutionLogs([]);
      }
      return;
    }

    // Restore original look and feel for logs, but handle objects and truncation
    const newStepLogs: Array<{
      id: string;
      timestamp: string;
      level: 'info' | 'warning' | 'error';
      message: string;
      isStepOutput?: boolean;
      isFinalOutput?: boolean;
      stepOrder?: number;
      details?: any;
      rawData?: any;
      rawResult?: any;
    }> = runResults.map((result) => {
      let outputPreview = '';
      if (typeof result.final_output === 'object' && result.final_output !== null) {
        try {
          outputPreview = JSON.stringify(result.final_output).slice(0, 100) + '...';
        } catch {
          outputPreview = '[Unserializable Object]';
        }
      } else if (typeof result.final_output === 'string') {
        outputPreview = result.final_output.length > 100
          ? result.final_output.substring(0, 100) + '...'
          : result.final_output;
      } else if (result.final_output !== undefined && result.final_output !== null) {
        outputPreview = String(result.final_output);
      } else {
        outputPreview = 'No output';
      }
      return {
        id: `step-output-${result.run_result_id}`,
        timestamp: result.created_at || result.completed_at || result.started_at || '',
        level: (result.status === 'completed' ? 'info' : result.status === 'failed' ? 'error' : 'warning') as 'info' | 'error' | 'warning',
        message: `Step ${result.step_order} Output: ${outputPreview}`,
        isStepOutput: true,
        stepOrder: result.step_order,
        details: {
          stepId: result.orchestration_step_id,
          runResultId: result.run_result_id,
          status: result.status,
          startedAt: result.started_at,
          completedAt: result.completed_at,
          duration: result.completed_at
            ? ((new Date(result.completed_at).getTime() - new Date(result.started_at).getTime()) / 1000).toFixed(2) + 's'
            : 'N/A',
          inputData: result.input_data,
          finalOutput: result.final_output,
          errorMessage: result.error_message,
        },
        rawData: sanitizeData(result),
        rawResult: result, // Attach the full backend object
      };
    });

    // Add final output log only when execution is complete
    // Find the actual final step using robust selection criteria
    const finalResult = findFinalResult(runResults);
    // Only add final output log when execution status is 'completed' (or 'failed' if we want to show final output on failure)
    if (finalResult?.final_output && execution?.status === 'completed') {
      let finalOutputPreview = '';
      if (typeof finalResult.final_output === 'object' && finalResult.final_output !== null) {
        try {
          finalOutputPreview = JSON.stringify(finalResult.final_output).slice(0, 100) + '...';
        } catch {
          finalOutputPreview = '[Unserializable Object]';
        }
      } else if (typeof finalResult.final_output === 'string') {
        finalOutputPreview = finalResult.final_output.length > 100
          ? finalResult.final_output.substring(0, 100) + '...'
          : finalResult.final_output;
      } else if (finalResult.final_output !== undefined && finalResult.final_output !== null) {
        finalOutputPreview = String(finalResult.final_output);
      } else {
        finalOutputPreview = 'No output';
      }
      const finalOutputLog = {
        id: `final-output-${finalResult.run_result_id}`,
        timestamp: execution.lastUpdated ? (typeof execution.lastUpdated === 'string' ? execution.lastUpdated : new Date(execution.lastUpdated).toISOString()) : (execution?.runInstance?.updated_at || new Date().toISOString()),
        level: 'info' as const,
        message: `🎉 FINAL OUTPUT: ${finalOutputPreview}`,
        isStepOutput: false,
        isFinalOutput: true,
        stepOrder: runResults.length,
        details: {
          stepId: '',
          runResultId: '',
          status: '' as any,
          startedAt: '',
          completedAt: '',
          duration: '',
          inputData: undefined,
          finalOutput: finalResult.final_output,
          errorMessage: undefined,
        },
        rawData: sanitizeData(finalResult),
        rawResult: finalResult,
      };
      newStepLogs.push(finalOutputLog as typeof newStepLogs[0]);
    }

    // Add error log if workflow failed and no error log is present
    if (execution?.status === 'failed') {
      const alreadyHasError = newStepLogs.some(log => log.level === 'error');
      if (!alreadyHasError) {
        const fallbackRawResult = finalResult || runResults[0] || {};
        const errorMessage = execution?.error || 'Workflow failed to complete';
        const errorDetails = execution?.error ? `Error: ${execution.error}` : '';
        const pollErrorInfo = execution?.pollErrorCount ? `Poll errors: ${execution.pollErrorCount}` : '';
        
        newStepLogs.push({
          id: `workflow-failed-${Date.now()}`,
          timestamp: new Date().toISOString(),
          level: 'error' as const,
          message: `❌ ${errorMessage}`,
          isStepOutput: false,
          stepOrder: runResults.length + 1,
          details: {
            error: execution?.error,
            pollErrorCount: execution?.pollErrorCount,
            lastUpdated: execution?.lastUpdated,
            errorDetails: errorDetails,
            pollErrorInfo: pollErrorInfo,
            runInstanceId: execution?.runInstance?.run_instance_id,
            workflowId: workflowId,
          } as any,
          rawData: sanitizeData(execution),
          rawResult: fallbackRawResult,
        });
      }
    }

    // Use functional setState to preserve initial log without adding executionLogs to dependencies
    setExecutionLogs(prevLogs => {
      // Check if we have an initial log (from execution start) to preserve
      const hasInitialLog = prevLogs.length > 0 &&
        prevLogs[0].id.startsWith('start-') &&
        !prevLogs[0].isStepOutput;

      // Check if final output log already exists to prevent duplicates
      const existingFinalOutputLog = prevLogs.find(log => log.isFinalOutput || log.id.startsWith('final-output-'));

      // Extract final output log from newStepLogs (if execution just completed)
      const newFinalOutputLog = newStepLogs.find(log => (log as any).isFinalOutput || log.id.startsWith('final-output-'));

      // Filter out final output logs from newStepLogs (we'll handle it separately)
      const filteredNewStepLogs = newStepLogs.filter(log => !(log as any).isFinalOutput && !log.id.startsWith('final-output-'));

      // Build final logs array — always include start log if we have a run ID
      const finalLogs: typeof executionLogs = [];
      if (hasInitialLog) {
        finalLogs.push(prevLogs[0]);
      } else if (currentRunInstanceId && !selectedHistoryRun) {
        // Re-create the start log if it was lost during state transitions
        finalLogs.push({
          id: `start-${currentRunInstanceId}`,
          timestamp: new Date().toISOString(),
          level: 'info' as const,
          message: `Workflow execution started - Run ID: ${String(currentRunInstanceId).slice(-8)}`,
        });
      }
      finalLogs.push(...filteredNewStepLogs);
      
      // Add final output log: use the one with highest step_order (the actual final step)
      // This handles the case where Step 1 completes first, then Step 2 arrives later
      if (existingFinalOutputLog && newFinalOutputLog) {
        // Compare step_order to determine which is the actual final step
        const existingStepOrder = (existingFinalOutputLog as any).rawResult?.step_order || 0;
        const newStepOrder = (newFinalOutputLog as any).rawResult?.step_order || 0;
        if (newStepOrder > existingStepOrder) {
          // New log has higher step_order - it's the actual final step, use it
          finalLogs.push(newFinalOutputLog);
        } else {
          // Existing log has same or higher step_order, keep it
          finalLogs.push(existingFinalOutputLog);
        }
      } else if (existingFinalOutputLog) {
        // No new final output log, preserve existing one
        finalLogs.push(existingFinalOutputLog);
      } else if (newFinalOutputLog) {
        // Add new final output log if execution just completed and no existing one
        finalLogs.push(newFinalOutputLog);
      }

      return finalLogs;
    });

    // Auto-scroll to latest log if panel is visible and we have new logs
    if (newStepLogs.length > 0 && !selectedHistoryRun) {
      setTimeout(() => {
        const executionPanel = document.querySelector('[data-testid="execution-panel"]');
        if (executionPanel) {
          const logsContainer = executionPanel.querySelector('.overflow-y-auto');
          if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
          }
        }
      }, 100);
    }
  }, [runResults, execution, workflowId, sanitizeData]);

  // Add initial log when starting a new execution (when runResults is cleared and isExecuting is true)
  useEffect(() => {
    if (!execution) {
      return;
    }
    
    if (runResults.length === 0 && isExecuting && executionLogs.length === 0) {
      const newLog = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        level: 'info' as const,
        message: 'Starting workflow execution...'
      };
      setExecutionLogs([newLog]);
    }
  }, [runResults.length, isExecuting, executionLogs.length, execution]);

  // Fetch workflow summary when execution completes or historical run is selected (AC-1161)
  useEffect(() => {
    // When a historical run is selected, fetch its summary
    if (selectedHistoryRun) {
      apiService.getRunInstance(selectedHistoryRun).then(ri => {
        setWorkflowSummary(ri?.summary || null);
      }).catch(() => setWorkflowSummary(null));
      return;
    }
    // When current execution completes, fetch summary
    if (execution?.status === 'completed' && currentRunInstanceId && autoSummarizeEnabled && !workflowSummary) {
      apiService.getRunInstance(currentRunInstanceId).then(ri => {
        if (ri?.summary) {
          setWorkflowSummary(ri.summary);
        }
      }).catch(() => {});
    }
    // Clear summary when a new execution starts
    if (execution?.status === 'running' || !execution) {
      setWorkflowSummary(null);
    }
  }, [execution?.status, currentRunInstanceId, autoSummarizeEnabled, selectedHistoryRun]);

  // Monitor execution errors and add them to the logs
  useEffect(() => {
    if (!execution) {
      return;
    }
    
    if (execution?.error) {
      const errorLog = {
        id: `execution-error-${Date.now()}`,
        timestamp: new Date().toISOString(),
        level: 'error' as const,
        message: `❌ EXECUTION ERROR: ${execution.error}`,
        isStepOutput: false,
        details: {
          error: execution.error,
          pollErrorCount: execution.pollErrorCount,
          lastUpdated: execution.lastUpdated,
        },
        rawData: sanitizeData(execution),
      };
      
      setExecutionLogs(prev => {
        // Check if this error is already logged to avoid duplicates
        const errorAlreadyLogged = prev.some(log => 
          log.level === 'error' && 
          log.message.includes(execution.error || '')
        );
        
        if (errorAlreadyLogged) {
          return prev;
        }
        
        return [...prev, errorLog];
      });
    }
  }, [execution?.error, execution?.pollErrorCount, execution?.lastUpdated, execution, sanitizeData]);

  useEffect(() => {
    if (!execution) {
      return;
    }

    if (
      execution &&
      (execution.status === 'completed' || execution.status === 'failed') &&
      isExecuting
    ) {
      setIsExecuting(false); executingRef.current = false;
      // Refresh run history when execution completes
      loadRunHistory();
    }
  }, [execution?.status, isExecuting, loadRunHistory, execution]);

  // AC-1367: SSE-backed run status hook. This is the durable fix for the
  // stuck-spinner class of bugs - the hook's own logic (snapshot on connect,
  // revalidate on focus, terminal-on-event) means the UI converges on server
  // truth even if polling misses the transition. We forward the terminal
  // status into the workflow store so all the existing handlers downstream
  // (loadRunHistory, setIsExecuting, etc.) fire the same way they do today.
  //
  // AC-1414 (P5.3) — The AC-1383 manual `apiService.getRunInstanceResults`
  // fetch + manual workflowStore setState path that used to live here is
  // retired. The SSE handler now invalidates the React Query
  // `workflowExecution(id)` cache key; the active `useExecution(id)`
  // subscription on this component (line ~394) refetches on its own. We
  // mirror the freshly-fetched RQ snapshot back into the legacy
  // `useWorkflowStore.executions` map so the still-store-driven render
  // pipeline (runResults, progress, stepStatuses, etc.) updates without
  // the second apiService call. When polling moves into the hook layer
  // in a later phase, the mirror step disappears and the consumer reads
  // RQ data directly. See TD section 2.10.
  const sseRunStatus = useRunStatus(currentRunInstanceId);
  useEffect(() => {
    if (!workflowId || !sseRunStatus.status) return;
    if (!sseRunStatus.isTerminal) return;

    const exec = useWorkflowStore.getState().executions.get(workflowId);
    if (!exec) return;
    if (exec.status === sseRunStatus.status) return; // already in sync

    useWorkflowStore.setState((state) => {
      const next = new Map(state.executions);
      const current = next.get(workflowId);
      if (!current) return state;
      next.set(workflowId, {
        ...current,
        status: sseRunStatus.status as RunStatus,
        runInstance: sseRunStatus.runInstance ?? current.runInstance,
        isRunning: false,
        isPaused: false,
        lastUpdated: Date.now(),
      });
      return { executions: next };
    });

    // AC-1414: Invalidate the RQ execution cache and let the active
    // useExecution(workflowId) subscription refetch. `invalidateQueries`
    // resolves AFTER the active subscriber's refetch settles (RQ's
    // default behavior — see TanStack Query docs on invalidateQueries),
    // so we can `then()` straight onto the refresh promise and mirror
    // the freshly-fetched cache value into workflowStore in the same
    // tick. This is what closes the original AC-1383 bug (SSE-terminal
    // ahead of polling, runResults empty until a manual reload). When
    // polling lives in a hook in a later phase, the mirror block goes
    // away and consumers read directly from RQ data.
    const executionKey = queryKeys.workflowExecution(workflowId);
    queryClient
      .invalidateQueries({ queryKey: executionKey })
      .then(() => {
        const snapshot =
          queryClient.getQueryData<WorkflowExecutionSnapshot>(executionKey);
        if (!snapshot) return;
        useWorkflowStore.setState((state) => {
          const next = new Map(state.executions);
          const current = next.get(workflowId);
          if (!current) return state;
          const completedResults = snapshot.runResults.filter(
            (r) => r.status === 'completed',
          );
          next.set(workflowId, {
            ...current,
            runResults: snapshot.runResults,
            currentStep: completedResults.length,
            totalSteps: snapshot.runResults.length,
            progress:
              snapshot.runResults.length > 0
                ? (completedResults.length / snapshot.runResults.length) * 100
                : 0,
            stepStatuses: new Map(
              snapshot.runResults.map((r) => [
                String(r.orchestration_step_id),
                r.status as RunStatus,
              ]),
            ),
            stepErrors: new Map(
              snapshot.runResults
                .filter((r) => r.error_message)
                .map((r) => [String(r.orchestration_step_id), r.error_message!]),
            ),
            sessionMessages: snapshot.sessionMessages.length
              ? snapshot.sessionMessages
              : current.sessionMessages,
            sessionInfoMap:
              Object.keys(snapshot.sessionInfoMap).length > 0
                ? snapshot.sessionInfoMap
                : current.sessionInfoMap,
            lastUpdated: Date.now(),
          });
          return { executions: next };
        });
      })
      .catch((err) => {
        console.warn('[DEBUG] SSE terminal: failed to refresh run results', err);
      })
      .finally(() => {
        useWorkflowStore.getState().stopPolling(workflowId);
      });
    loadRunHistory();
  }, [sseRunStatus.status, sseRunStatus.isTerminal, sseRunStatus.runInstance, workflowId, loadRunHistory, queryClient]);

  // Sync isExecuting state with execution status on page load/reload
  useEffect(() => {
    if (execution && (execution.isRunning || execution.isPaused)) {
      setIsExecuting(true);
    } else if (execution && (execution.status === 'completed' || execution.status === 'failed' || execution.status === 'stopped')) {
      setIsExecuting(false); executingRef.current = false;
    }
  }, [execution]);

  // AC-1271: Re-sync execution status when the tab regains focus/visibility.
  // pollRunResults can stop (maxDuration, backoff errors, tab throttling) without
  // ever observing a terminal status, leaving the UI stuck in "Executing". On
  // focus/visibility we ask the backend for the authoritative run status and
  // reconcile the local store.
  useEffect(() => {
    if (!workflowId) return;

    const resync = async () => {
      const currentExec = useWorkflowStore.getState().executions.get(workflowId);
      const runInstanceId = currentExec?.runInstance?.run_instance_id;
      if (!runInstanceId) return;
      if (!currentExec.isRunning && !currentExec.isPaused) return;

      try {
        const fresh = await apiService.getRunInstance(String(runInstanceId));
        const freshStatus = fresh.status as RunStatus;
        const terminal = freshStatus === 'completed' || freshStatus === 'failed' || freshStatus === 'stopped';
        if (terminal && freshStatus !== currentExec.status) {
          useWorkflowStore.setState(state => {
            const execs = new Map(state.executions);
            const exec = execs.get(workflowId);
            if (exec) {
              exec.status = freshStatus;
              exec.isRunning = false;
              exec.isPaused = false;
              exec.lastUpdated = Date.now();
              execs.set(workflowId, exec);
            }
            return { executions: execs };
          });
          useWorkflowStore.getState().stopPolling(workflowId);
          loadRunHistory();
        }
      } catch (err) {
        console.warn('[AC-1271] Failed to resync execution status:', err);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') resync();
    };

    window.addEventListener('focus', resync);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', resync);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [workflowId, loadRunHistory]);

  return (
    <div className="flex flex-col h-full">
      {/* Compact Project and Workflow Name Header */}
      <div className="flex flex-col px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark relative overflow-visible">
        {/* Row 1: Project and Workflow Name */}
        <div className="flex flex-col gap-1 flex-1 w-full">
          {/* Breadcrumb and Name */}
          <div className="flex items-center gap-2">
            <span className="text-primary-700 font-semibold">{currentProject?.name || 'Project'}</span>
            <span className="text-gray-400">›</span>
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  ref={nameInputRef}
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      handleCancelEditName();
                    }
                  }}
                  className="px-2 py-1 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 font-semibold border border-blue-500 dark:border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
                <button
                  onClick={handleSaveName}
                  disabled={isSaving}
                  className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                >
                  <Tooltip content="Save">
                  <Check className="w-4 h-4" />
                  </Tooltip>
                </button>
                <button
                  onClick={handleCancelEditName}
                  disabled={isSaving}
                  className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  <Tooltip content="Cancel">
                  <X className="w-4 h-4" />
                  </Tooltip>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 group">
                <span className="text-gray-800 dark:text-gray-200 font-semibold">{workflowName || 'Workflow'}</span>
                <button
                  onClick={() => {
                    setIsEditingName(true);
                    setTempName(workflowName);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Tooltip content="Edit name">
                  <Edit2 className="w-3 h-3" />
                  </Tooltip>
                </button>
                <ContextualHelpIcon
                  docPath="features/workflows.md"
                  tooltip="Learn about orchestrations"
                  size="sm"
                />
              </div>
            )}
          </div>
          
          {/* Description */}
          <div className="flex items-start gap-1">
            {isEditingDescription ? (
              <div className="flex items-start gap-2 w-full">
                <textarea
                  ref={descriptionTextareaRef}
                  value={tempDescription}
                  onChange={(e) => setTempDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleSaveDescription();
                    } else if (e.key === 'Escape') {
                      handleCancelEditDescription();
                    }
                  }}
                  className="flex-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-200 bg-white dark:bg-gray-800 border border-blue-500 dark:border-blue-400 rounded resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Add a description..."
                  autoFocus
                />
                <div className="flex flex-col gap-1">
                  <button
                    onClick={handleSaveDescription}
                    disabled={isSaving}
                    className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                  >
                    <Tooltip content="Save (Ctrl+Enter)">
                    <Check className="w-4 h-4" />
                    </Tooltip>
                  </button>
                  <button
                    onClick={handleCancelEditDescription}
                    disabled={isSaving}
                    className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    <Tooltip content="Cancel (Esc)">
                    <X className="w-4 h-4" />
                    </Tooltip>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-1 group w-full">
                <span className="text-sm text-gray-600 dark:text-gray-400 italic">
                  {workflowDescription || 'No description'}
                </span>
                <button
                  onClick={() => {
                    setIsEditingDescription(true);
                    setTempDescription(workflowDescription);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                >
                  <Tooltip content="Edit description">
                  <Edit2 className="w-3 h-3" />
                  </Tooltip>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Row 2: Execution Controls */}
        <div className="flex items-center flex-wrap gap-3 mt-3 relative">
          {/* AC-1702: open the Agent Commander copilot scoped to this workflow.
              The editor sets contextType='orchestration' on mount, so the panel
              opens already bound to the open workflow and edits reflect live on
              the canvas via the existing assistant-action listener. */}
          <Tooltip content={`Adjust this workflow with ${ASSISTANT_NAME}`}>
          <button
            onClick={openAssistant}
            data-testid="assistant-launcher-editor"
            data-tutorial="btn-edit-with-assistant"
            className="flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Logo variant="static-transparent" size={20} />
            <span>Edit with {ASSISTANT_NAME}</span>
          </button>
          </Tooltip>

          {/* Execution Status */}
          {isExecuting && (
            <div className="flex items-center space-x-2 text-primary-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">Executing...</span>
            </div>
          )}
          
          {/* Error Status */}
          {execution?.error && isExecuting && (
            <div className="flex items-center space-x-2 text-red-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">Error Detected</span>
            </div>
          )}
          
          {/* Execution Panel Toggle */}
          {executionLogs.length > 0 && (
            <button
              onClick={() => setShowExecutionPanel(!showExecutionPanel)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
                showExecutionPanel
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                  : 'bg-primary-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-primary-100 dark:hover:bg-blue-900/40'
              }`}
            >
              <Tooltip content="Toggle Execution Panel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              </Tooltip>
              <span>Logs ({executionLogs.length})</span>
            </button>
          )}
          
          {/* Run History Toggle */}
          {runHistory.length > 0 && (
            <div className="relative run-history-dropdown">
              <button
                onClick={() => setShowRunHistory(!showRunHistory)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
                  showRunHistory
                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700'
                    : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/40'
                }`}
              >
                <Tooltip content="View Run History">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                </Tooltip>
                <span>History ({runHistory.length})</span>
                <svg className={`w-3 h-3 transition-transform ${showRunHistory ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Run History Dropdown */}
              {showRunHistory && (
                <div className="absolute left-0 top-full mt-1 w-[calc(100vw-2rem)] sm:w-80 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[100] max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-white">Run History</h3>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            loadRunHistory();
                          }}
                          className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Tooltip content="Refresh run history">
                          🔄 Refresh
                          </Tooltip>
                        </button>
                        <button
                          onClick={() => {
                            setShowLogManagementPanel(true);
                            setShowRunHistory(false);
                          }}
                          className="text-xs text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 px-2 py-1 rounded hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors flex items-center space-x-1"
                        >
                          <Tooltip content="Manage Execution Logs">
                          <Database className="w-3 h-3" />
                          </Tooltip>
                          <span>Log Management</span>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Click on any run to view its logs</p>
                  </div>
                  <div className="p-2">
                    {runHistory.map((run) => (
                      <div key={run.run_instance_id} className={`rounded-md border-l-4 ${
                        selectedHistoryRun === run.run_instance_id
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : run.status === 'completed'
                          ? 'border-green-400'
                          : run.status === 'failed'
                          ? 'border-red-400'
                          : 'border-yellow-400'
                      }`}>
                        <button
                          onClick={() => {
                            console.log('Clicked on historical run:', run.run_instance_id);
                            loadHistoricalLogs(run.run_instance_id);
                            setShowRunHistory(false);
                          }}
                          className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                              Run {String(run.run_instance_id).slice(-8)}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              run.status === 'completed'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : run.status === 'failed'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}>
                              {run.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            <div>Started: {new Date(run.created_at).toLocaleString()}</div>
                            {run.completed_at && (
                              <div>Completed: {new Date(run.completed_at).toLocaleString()}</div>
                            )}
                            {run.completed_at && (
                              <div className="text-gray-500 dark:text-gray-400">
                                Duration: {((new Date(run.completed_at).getTime() - new Date(run.created_at).getTime()) / 1000).toFixed(2)}s
                              </div>
                            )}
                          </div>
                        </button>
                        {run.status === 'completed' && (
                          <div className="px-3 pb-2">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const response = await apiService.getRunInstanceResults(run.run_instance_id);
                                  const runResults = response.results;
                                  const finalResult = runResults[runResults.length - 1];
                                  if (finalResult?.final_output) {
                                    setFinalOutputData({
                                      runResult: finalResult,
                                      duration: run.completed_at ?
                                        ((new Date(run.completed_at).getTime() - new Date(run.created_at).getTime()) / 1000).toFixed(2) + 's' : 'N/A',
                                      totalSteps: runResults.length,
                                      timestamp: finalResult.completed_at || finalResult.started_at,
                                    });
                                    setShowFinalOutputModal(true);
                                    setShowRunHistory(false);
                                  }
                                } catch (error) {
                                  console.error('Failed to load final output:', error);
                                }
                              }}
                              className="text-xs text-primary-600 hover:text-primary-800 px-2 py-1 rounded hover:bg-primary-100 transition-colors font-medium border border-primary-300"
                            >
                              📄 Quick View Final Output
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* AC-1517: BYOK cannot-run banner.
              Appears above the Execute button when the workflow uses agents
              whose providers the user hasn't configured. The banner stays
              inline with the toolbar so it never displaces canvas elements. */}
          {hasMissingKeys && (
            <div
              role="alert"
              data-testid="byok-cannot-run-banner"
              className="flex items-start gap-2 px-3 py-2 mr-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md max-w-md"
            >
              <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div className="text-xs text-yellow-800 dark:text-yellow-200">
                <p>
                  This workflow uses agents whose providers need a key:{' '}
                  <span className="font-semibold">
                    {missingKeyProviders.map(providerDisplayName).join(', ')}
                  </span>
                  .
                </p>
                <p className="mt-1">
                  <a
                    href="/settings/api-keys"
                    className="underline font-medium hover:no-underline"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/settings/api-keys');
                    }}
                  >
                    Add a key in Settings &raquo; API Keys
                  </a>{' '}
                  to run this workflow.
                </p>
              </div>
            </div>
          )}

          {/* Execution Buttons */}
          {!isExecuting && !waitingForUserInput ? (
            <>
              <Tooltip
                content={
                  hasMissingKeys
                    ? `Add an API key for: ${missingKeyProviders
                        .map(providerDisplayName)
                        .join(', ')}`
                    : canExecuteOrSchedule
                      ? 'Execute Workflow'
                      : 'Add an initiator step (User Prompt, Auto Prompt, or Queue Read) to execute this workflow'
                }
              >
              <button
                onClick={handleExecuteWorkflow}
                disabled={!canExecuteOrSchedule || hasMissingKeys}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  canExecuteOrSchedule && !hasMissingKeys
                    ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
                data-tutorial="btn-run-workflow"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h1m4 0h1M9 6h6a2 2 0 012 2v8a2 2 0 01-2 2H9a2 2 0 01-2-2V8a2 2 0 012-2z" />
                </svg>
                <span>Execute</span>
              </button>
              </Tooltip>
            </>
          ) : (isExecuting || waitingForUserInput) ? (
            <>
              <button
                onClick={handleStopExecution}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <Tooltip content="Stop Execution">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
                </Tooltip>
                <span>Stop</span>
              </button>
            </>
          ) : null}

          {/* Clear Session Button - available regardless of execution state */}
          {workflowId && (
            <ClearSessionButton
              workflowId={workflowId}
              isDisabled={false}
            />
          )}

          {/* View Mode Switcher */}
          <ViewModeSwitcher
            currentMode={viewMode}
            onModeChange={setViewMode}
            hasAssistantSteps={hasAssistantSteps}
          />

          {/* Version History Button with Badge */}
          {workflowId && currentProject && (
            <button
              onClick={() => {
                const newShowState = !showVersionHistory;
                setShowVersionHistory(newShowState);
                // When opening the panel, capture previous lastViewed for highlighting new items
                // then update to current time (clears badge)
                if (newShowState) {
                  // Set highlight threshold to previous lastViewed (items newer than this get "New" badge)
                  setHighlightChangesSince(historyLastViewed);
                  // Update last viewed to now
                  const now = new Date().toISOString();
                  localStorage.setItem(getLastViewedKey(), now);
                  setHistoryLastViewed(now);
                } else {
                  // Clear highlight when closing panel
                  setHighlightChangesSince(null);
                }
              }}
              role="tab"
              aria-selected={showVersionHistory}
              className={`relative flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
                showVersionHistory
                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700'
                  : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
              }`}
            >
              <Tooltip content={recentChangeCount > 0 ? `View Version History (${recentChangeCount} new changes)` : 'View Version History'}>
              <History className="w-4 h-4" />
              </Tooltip>
              <span>History</span>
              {recentChangeCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-5 h-5 px-1 text-xs font-bold text-white bg-indigo-600 rounded-full">
                  {recentChangeCount > 99 ? '99+' : recentChangeCount}
                </span>
              )}
            </button>
          )}

          {/* Schedule Button or Queue Link (AC-481) */}
          {workflowId && (() => {
            // AC-481: Show Queue button for queue-read orchestrations, Schedule button for others
            if (hasQueueReadAsFirstStep) {
              // Build tooltip for queue button
              const getQueueTooltipText = () => {
                if (isLoadingSourceQueue) {
                  return 'Loading queue details...';
                }
                if (!sourceQueue) {
                  return 'Queue not configured - Configure the queue_read step first';
                }

                const parts: string[] = [`Queue: ${sourceQueue.name}`];

                if (sourceQueue.auto_trigger_enabled) {
                  parts.push('Auto-trigger: Enabled');
                  if (sourceQueue.trigger_interval_minutes) {
                    parts.push(`Interval: ${sourceQueue.trigger_interval_minutes} min`);
                  }
                  if (sourceQueue.trigger_item_threshold) {
                    parts.push(`Item threshold: ${sourceQueue.trigger_item_threshold}`);
                  }
                  if (sourceQueue.trigger_age_threshold_minutes) {
                    parts.push(`Age threshold: ${sourceQueue.trigger_age_threshold_minutes} min`);
                  }
                  if (sourceQueue.last_triggered_at) {
                    const lastTrigger = new Date(sourceQueue.last_triggered_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    });
                    parts.push(`Last triggered: ${lastTrigger}`);
                  }
                } else {
                  parts.push('Auto-trigger: Disabled');
                }

                return parts.join('\n');
              };

              // Determine button styling based on queue trigger state
              const getQueueButtonClasses = () => {
                if (!sourceQueue) {
                  return 'bg-gray-100 dark:bg-gray-700 text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed';
                }
                if (sourceQueue.auto_trigger_enabled) {
                  // Trigger enabled - green styling
                  return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-300 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40';
                } else {
                  // Trigger disabled - muted styling
                  return 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700';
                }
              };

              const handleQueueClick = () => {
                if (sourceQueue && projectId) {
                  navigateWithContext(
                    `/projects/${projectId}/queues/${sourceQueue.queue_id}`,
                    'Workflow'
                  );
                }
              };

              return (
                <Tooltip content={getQueueTooltipText()}>
                <button
                  onClick={handleQueueClick}
                  disabled={!sourceQueue}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${getQueueButtonClasses()}`}
                >
                  <Layers className="w-4 h-4" />
                  <span>{isLoadingSourceQueue ? 'Loading...' : (sourceQueue?.name || 'Queue')}</span>
                  {sourceQueue && (
                    <span
                      className={`w-2 h-2 rounded-full ${
                        sourceQueue.auto_trigger_enabled ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                      aria-label={sourceQueue.auto_trigger_enabled ? 'Auto-trigger enabled' : 'Auto-trigger disabled'}
                    />
                  )}
                </button>
                </Tooltip>
              );
            }

            // Regular Schedule button for non-queue-read orchestrations
            const isScheduleDisabled = !canExecuteOrSchedule;

            // AC-473: Determine button styling based on schedule state and workflow completeness
            const getButtonClasses = () => {
              // Disabled state when no initiator step
              if (isScheduleDisabled) {
                return 'bg-gray-100 dark:bg-gray-700 text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed';
              }
              if (!currentSchedule) {
                // No schedule - default indigo styling
                return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/40';
              } else if (currentSchedule.is_enabled) {
                // Schedule enabled - green styling
                return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-300 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40';
              } else {
                // Schedule disabled - muted gray styling
                return 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700';
              }
            };

            // Build tooltip text
            const getTooltipText = () => {
              // AC-473: Show message when workflow is incomplete
              if (!canExecuteOrSchedule) {
                return 'Add an initiator step (User Prompt, Auto Prompt, or Queue Read) to schedule this workflow';
              }
              if (!currentSchedule) {
                return 'Configure schedule for this workflow';
              } else if (currentSchedule.is_enabled) {
                const nextRun = currentSchedule.next_run_at
                  ? new Date(currentSchedule.next_run_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : 'Not scheduled';
                return `Schedule enabled - Click to configure (Next run: ${nextRun})`;
              } else {
                return 'Schedule paused - Click to configure';
              }
            };

            return (
              <button
                onClick={() => setShowScheduleModal(true)}
                disabled={isScheduleDisabled}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${getButtonClasses()}`}
                data-tutorial="btn-schedule-workflow"
              >
                <Tooltip content={getTooltipText()}>
                <Clock className="w-4 h-4" />
                </Tooltip>
                <span>Schedule</span>
                {currentSchedule && !isScheduleDisabled && (
                  <span
                    className={`w-2 h-2 rounded-full ${
                      currentSchedule.is_enabled ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                    aria-label={currentSchedule.is_enabled ? 'Schedule enabled' : 'Schedule disabled'}
                  />
                )}
              </button>
            );
          })()}

          {/* WhatsApp Notification Toggle (AC-1051) - only show when connected */}
          {workflowId && whatsappConnected && (
            <WhatsAppNotificationToggle
              orchestrationId={parseInt(workflowId, 10)}
              enabled={whatsappNotifyEnabled}
              onToggle={setWhatsappNotifyEnabled}
            />
          )}

          {/* Auto-Summarize Toggle (AC-1161) */}
          {workflowId && (
            <AutoSummarizeToggle
              orchestrationId={parseInt(workflowId, 10)}
              enabled={autoSummarizeEnabled}
              onToggle={setAutoSummarizeEnabled}
            />
          )}

          {/* Delivery Channel Setting (AC-1816, epic AC-1813) */}
          {workflowId && (
            <DeliveryChannelSetting
              orchestrationId={parseInt(workflowId, 10)}
              channel={deliveryChannel}
              destination={deliveryDestination}
              credentialId={deliveryCredentialId}
              availableChannels={availableChannels}
              onChange={(next) => {
                setDeliveryChannel(next.channel);
                setDeliveryDestination(next.destination);
                setDeliveryCredentialId(next.credentialId);
              }}
            />
          )}

          {/* Chat Mode Indicator */}
          {hasAssistantSteps && showChatInterface && (
            <div className="flex items-center space-x-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>Assistant Chat Mode</span>
            </div>
          )}
        </div>
      </div>

      {/* User Input Panel - Only show when NOT in chat mode */}
      {waitingForUserInput && !showChatInterface && (
        <div className="px-6 py-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Workflow is waiting for your input</span>
              </div>
              <p className="text-sm text-yellow-700 dark:text-yellow-400 mb-3">{waitingForUserInput.message}</p>
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={userInputValue}
                  onChange={(e) => setUserInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSubmitUserInput()}
                  placeholder="Enter your response..."
                  className="flex-1 px-3 py-2 border border-yellow-300 dark:border-yellow-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
                <button
                  onClick={handleSubmitUserInput}
                  disabled={!userInputValue.trim()}
                  className="px-4 py-2 bg-yellow-600 dark:bg-yellow-700 text-white rounded-md hover:bg-yellow-700 dark:hover:bg-yellow-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                  data-tutorial="btn-submit-user-input"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Component Library */}
      {componentLibraryOpen && (
        <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark">
          <ComponentLibrary agents={agents} models={models} onComponentClick={handleComponentClick} />
        </div>
      )}

      {/* Main Content Area */}
      {!isLogsFocused && (
        <div className="flex-1 relative min-h-0 overflow-hidden flex flex-col">
          {viewMode === 'workflow' ? (
            // Workflow View
            isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500 dark:text-gray-400">Loading workflow...</div>
              </div>
            ) : (
              <ReactFlowProvider>
                <FlowContent
                  nodes={allNodes}
                  edges={allEdges}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={handleEdgesChange}
                  onConnect={onConnect}
                  onNodeClick={onNodeClick}
                  onNodeContextMenu={handleNodeContextMenu}
                  onEdgeClick={onEdgeClick}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onNodeDragStop={handleNodeDragStop}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  defaultEdgeOptions={defaultEdgeOptions}
                  snapToGridEnabled={snapToGridEnabled}
                  gridSize={GRID_SIZE}
                  gridEnabled={gridEnabled}
                  onAutoLayout={handleAutoLayout}
                  onCompactLayout={handleCompactLayout}
                  onAlignNodes={handleAlignNodes}
                  onDistributeNodes={handleDistributeNodes}
                  onToggleGrid={() => setGridEnabled(!gridEnabled)}
                  onToggleSnapToGrid={() => setSnapToGridEnabled(!snapToGridEnabled)}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  selectedNodesCount={selectedNodes.length}
                  workflowId={workflowId}
                />
              </ReactFlowProvider>
            )
          ) : (
            // Chat View
            assistantStep && execution && execution.runInstance && workflowId && (
              <div className="flex-1 p-6 min-h-0 overflow-hidden flex flex-col" style={{ height: showExecutionPanel ? 'calc(100vh - 200px)' : 'calc(100vh - 120px)' }}>
                <ChatInterface
                  workflow={{ orchestration_id: workflowId } as any}
                  runInstance={execution.runInstance}
                  assistantStep={assistantStep}
                  sessionConfig={assistantStep?.session_config}
                  onMessageSend={handleChatMessageSend}
                  className="h-full overflow-hidden"
                />
              </div>
            )
          )}

        {/* Step Configuration Modal */}
        {(() => {
          if (!showStepModal) return null;
          const fromNodeId = stepModalContext?.fromNodeId;
          const node = fromNodeId ? nodes.find(n => n.id === fromNodeId) : undefined;
          const prevStepType = node?.data?.step?.step_type;
          const isEditing = stepModalContext?.isEditing;
          const existingStep = isEditing ? node?.data?.step : undefined;
          return (
            <StepConfigurationModal
              isOpen={showStepModal}
              onClose={() => { setShowStepModal(false); setStepModalContext(null); }}
              onSave={handleSaveStep}
              prevStepType={prevStepType}
              agents={agents}
              existingStep={existingStep}
              isEditing={isEditing}
              previousNodeId={fromNodeId} // Pass the fromNodeId as previousNodeId
              onConvertToAutoPrompt={handleConvertToAutoPrompt}
              onConvertToFilter={handleConvertToFilter}
            />
          );
        })()}

        {/* Validation Panel */}
        {validationErrors.length > 0 && (
          <div className="absolute bottom-4 left-4 right-4">
            <ValidationPanel
              errors={validationErrors.map(e => e.message)}
              isValidating={isValidating}
            />
          </div>
        )}
      </div>
      )}

      {/* Execution Panel */}
      {showExecutionPanel && executionLogs.length > 0 && (
        <div data-testid="execution-panel" className={`${isLogsFocused ? 'flex-1' : 'h-64'} border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark flex flex-col`}>
          <div className={`flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 ${selectedHistoryRun ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-gray-50 dark:bg-gray-900'}`}>
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">Execution Logs</h3>
              {selectedHistoryRun && (
                <span className="text-xs bg-purple-200 dark:bg-purple-900/40 text-purple-900 dark:text-purple-300 px-3 py-1 rounded-full font-bold border border-purple-300 dark:border-purple-700">
                  📜 HISTORICAL RUN {String(selectedHistoryRun).slice(-8)}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {/* AC-760: Show Quick View button for both historical runs AND real-time completions */}
              {(selectedHistoryRun || finalOutputData) && executionLogs.some(log => log.isFinalOutput && log.details?.finalOutput) && (
                <button
                  onClick={() => {
                    // If finalOutputData already exists (real-time completion), use it directly
                    if (finalOutputData) {
                      setShowFinalOutputModal(true);
                      return;
                    }

                    // Otherwise, extract from logs (historical run case)
                    const finalLog = executionLogs.find(log => log.isFinalOutput && log.details?.finalOutput);
                    if (finalLog) {
                      // Try to get RunResult from log.rawResult or log.rawData
                      let finalRunResult: RunResult | null = null;
                      if (finalLog.rawResult) {
                        finalRunResult = finalLog.rawResult as RunResult;
                      } else if (finalLog.rawData) {
                        const rawData = finalLog.rawData as any;
                        if (rawData.finalResult) {
                          finalRunResult = rawData.finalResult as RunResult;
                        } else if (rawData.runResults && Array.isArray(rawData.runResults) && rawData.runResults.length > 0) {
                          // Find the actual final step using robust selection criteria
                          finalRunResult = findFinalResult(rawData.runResults as RunResult[]) || null;
                        }
                      }

                      // If we have a RunResult, use it; otherwise create a minimal one from available data
                      if (!finalRunResult && finalLog.details?.finalOutput) {
                        // Try to find the final step from nodes if stepId is missing
                        let stepId = finalLog.details.stepId || '';
                        if (!stepId && nodes.length > 0) {
                          // Find the last step (highest step_order) or the step with no next step
                          const finalNode = nodes
                            .filter(node => node.data?.step)
                            .sort((a, b) => (b.data.step.step_order || 0) - (a.data.step.step_order || 0))[0];
                          if (finalNode?.data?.step?.orchestration_step_id) {
                            stepId = String(finalNode.data.step.orchestration_step_id);
                          }
                        }

                        // Fallback: create minimal RunResult from available data
                        finalRunResult = {
                          run_result_id: finalLog.details.runResultId || '',
                          run_instance_id: '',
                          orchestration_step_id: stepId,
                          step_order: finalLog.details.totalSteps || 0,
                          status: 'completed',
                          final_output: finalLog.details.finalOutput,
                          started_at: finalLog.details.startedAt || finalLog.timestamp,
                          completed_at: finalLog.details.completedAt || finalLog.timestamp,
                          created_at: finalLog.timestamp,
                        } as RunResult;
                      }

                      if (finalRunResult) {
                        setFinalOutputData({
                          runResult: finalRunResult,
                          duration: finalLog.details.totalDuration || 'N/A',
                          totalSteps: finalLog.details.totalSteps || 0,
                          timestamp: finalLog.timestamp,
                        });
                        setShowFinalOutputModal(true);
                      }
                    }
                  }}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 px-3 py-1 rounded hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors font-medium border border-primary-300 dark:border-primary-700"
                  aria-label="Quick View"
                >
                  <Tooltip content="Quick view final output">📄 Quick View</Tooltip>
                </button>
              )}
              {/* View Summary button (AC-1161) — only shown when auto-summary exists */}
              {workflowSummary && (
                <button
                  onClick={() => setShowSummaryModal(true)}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 px-2 py-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors font-medium border border-purple-300 dark:border-purple-700"
                >
                  <Tooltip content="View auto-generated workflow summary">📊 View Summary</Tooltip>
                </button>
              )}
              {selectedHistoryRun && (
                <button
                  onClick={() => {
                    setExecutionLogs([]);
                    setSelectedHistoryRun(null);
                    setShowExecutionPanel(false);
                    setIsLogsFocused(false);
                  }}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 px-2 py-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                >
                  <Tooltip content="Clear historical logs">Clear History</Tooltip>
                </button>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {executionLogs.length} entries
              </span>
              <button
                onClick={() => setIsLogsFocused(!isLogsFocused)}
                className={`flex items-center space-x-1 px-2 py-1 text-xs rounded transition-colors ${
                  isLogsFocused
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-primary-700'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Tooltip content={isLogsFocused ? "Show design view" : "Focus on logs only"}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isLogsFocused ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  )}
                </svg>
                </Tooltip>
                <span>{isLogsFocused ? 'Exit Focus' : 'Focus Logs'}</span>
              </button>
              <button
                onClick={() => {
                  setExecutionLogs([]);
                  setIsLogsFocused(false);
                }}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
              >
                <Tooltip content="Clear logs">Clear</Tooltip>
              </button>
              <button
                onClick={() => {
                  setShowExecutionPanel(false);
                  setIsLogsFocused(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
              >
                <Tooltip content="Close panel">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                </Tooltip>
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3" data-testid="execution-logs">
            <div className="space-y-2">
              {executionLogs.map((log) => {
                const isExpanded = expandedLogs.has(log.id);
                const hasDetails = log.details || log.rawData;
                
                // Use InlineOutputDisplay for step outputs
                if (log.isStepOutput && (log.rawResult || log.details?.runResultId)) {
                  // Only render InlineOutputDisplay, do not render parent expand/collapse arrow or handler
                  const runResult = log.rawResult || {
                    ...log.details,
                    id: log.details.runResultId,
                    created_at: log.details.created_at || log.details.createdAt || log.details.startedAt || log.timestamp,
                    run_result_id: log.details.runResultId,
                    run_instance_id: log.details.run_instance_id || '',
                    orchestration_step_id: log.details.stepId,
                    step_order: log.stepOrder || 0,
                    status: log.details.status || 'completed',
                    input_data: log.details.inputData,
                    final_output: log.details.finalOutput,
                    error_message: log.details.errorMessage,
                    started_at: log.details.startedAt || log.timestamp,
                    completed_at: log.details.completedAt,
                  };
                  
                  // Extract workflow steps from nodes for step type mapping
                  const workflowSteps = nodes
                    .filter(node => node.data?.step)
                    .map(node => ({
                      orchestration_step_id: node.data.step.orchestration_step_id,
                      step_type: node.data.step.step_type,
                      description: node.data.step.description,
                      label: node.data.step.label,
                      agent_id: node.data.step.agent_id,
                      config: node.data.step.config, // Include step-specific config (e.g., for queue_read, queue_write)
                    }));
                  
                  return (
                    <InlineOutputDisplay
                      key={log.id}
                      runResult={runResult}
                      onViewFullOutput={handleViewFullOutput}
                      maxPreviewLength={150}
                      showMetadata={true}
                      workflowSteps={workflowSteps}
                      agents={agents}
                      onEditAgent={handleEditAgent}
                    />
                  );
                }
                
                // Regular log entry rendering (non-step outputs)
                return (
                  <div
                    key={log.id}
                    className={`text-sm p-3 rounded-lg border transition-all duration-200 ${
                      log.details?.isHistoricalNotification
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-200 border-purple-400 dark:border-purple-800 shadow-lg border-2'
                        : log.isFinalOutput
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-200 border-green-300 dark:border-green-800 shadow-md'
                        : log.level === 'error'
                        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800'
                        : log.level === 'warning'
                        ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800'
                        : 'bg-primary-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800'
                    } ${hasDetails ? 'cursor-pointer hover:shadow-md' : ''}`}
                    onClick={() => hasDetails && toggleLogExpansion(log.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
                        log.details?.isHistoricalNotification
                          ? 'bg-purple-600'
                          : log.isFinalOutput
                          ? 'bg-green-600'
                          : log.level === 'error'
                          ? 'bg-red-500'
                          : log.level === 'warning'
                          ? 'bg-yellow-500'
                          : 'bg-blue-500'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className={`font-bold uppercase text-xs px-2 py-1 rounded ${
                              log.details?.isHistoricalNotification
                                ? 'bg-purple-300 dark:bg-purple-800/60 text-purple-900 dark:text-purple-200'
                                : log.isFinalOutput
                                ? 'bg-green-200 dark:bg-green-800/60 text-green-800 dark:text-green-200'
                                : log.level === 'error'
                                ? 'bg-red-200 dark:bg-red-800/60 text-red-800 dark:text-red-200'
                                : log.level === 'warning'
                                ? 'bg-yellow-200 dark:bg-yellow-800/60 text-yellow-800 dark:text-yellow-200'
                                : 'bg-blue-200 dark:bg-blue-800/60 text-blue-800 dark:text-blue-200'
                            }`}>
                              {log.details?.isHistoricalNotification ? 'HISTORICAL' : log.isFinalOutput ? 'FINAL' : log.level}
                            </span>
                            {log.isFinalOutput && (
                              <span className="text-xs font-semibold text-green-700 dark:text-green-200 bg-green-200 dark:bg-green-800/60 px-2 py-1 rounded">
                                🎉 COMPLETED
                              </span>
                            )}
                            {/* Inline View Summary button next to COMPLETED badge */}
                            {log.isFinalOutput && workflowSummary && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowSummaryModal(true);
                                }}
                                className="text-xs font-semibold text-purple-700 dark:text-purple-200 bg-purple-200 dark:bg-purple-800/60 px-2 py-1 rounded hover:bg-purple-300 dark:hover:bg-purple-800/80 transition-colors"
                              >
                                📊 View Summary
                              </button>
                            )}
                            {/* AC-759: Inline Quick View button for final output log entries */}
                            {log.isFinalOutput && log.details?.finalOutput && (
                              <button
                                data-testid="quick-view-final-output-btn"
                                data-tutorial="btn-view-final-output"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Try to get RunResult from log.rawResult or log.rawData
                                  let finalRunResult: RunResult | null = null;
                                  if (log.rawResult) {
                                    finalRunResult = log.rawResult as RunResult;
                                  } else if (log.rawData) {
                                    const rawData = log.rawData as any;
                                    if (rawData.finalResult) {
                                      finalRunResult = rawData.finalResult as RunResult;
                                    } else if (rawData.runResults && Array.isArray(rawData.runResults) && rawData.runResults.length > 0) {
                                      // Find the actual final step using robust selection criteria
                                      finalRunResult = findFinalResult(rawData.runResults as RunResult[]) || null;
                                    }
                                  }

                                  // If we have a RunResult, use it; otherwise create a minimal one from available data
                                  if (!finalRunResult && log.details?.finalOutput) {
                                    // Try to find the final step from nodes if stepId is missing
                                    let stepId = log.details.stepId || '';
                                    if (!stepId && nodes.length > 0) {
                                      // Find the last step (highest step_order) or the step with no next step
                                      const finalNode = nodes
                                        .filter(node => node.data?.step)
                                        .sort((a, b) => (b.data.step.step_order || 0) - (a.data.step.step_order || 0))[0];
                                      if (finalNode?.data?.step?.orchestration_step_id) {
                                        stepId = String(finalNode.data.step.orchestration_step_id);
                                      }
                                    }

                                    // Fallback: create minimal RunResult from available data
                                    finalRunResult = {
                                      run_result_id: log.details.runResultId || '',
                                      run_instance_id: '',
                                      orchestration_step_id: stepId,
                                      step_order: log.details.totalSteps || 0,
                                      status: 'completed',
                                      final_output: log.details.finalOutput,
                                      started_at: log.details.startedAt || log.timestamp,
                                      completed_at: log.details.completedAt || log.timestamp,
                                      created_at: log.timestamp,
                                    } as RunResult;
                                  }

                                  if (finalRunResult) {
                                    setFinalOutputData({
                                      runResult: finalRunResult,
                                      duration: log.details.totalDuration || 'N/A',
                                      totalSteps: log.details.totalSteps || 0,
                                      timestamp: log.timestamp,
                                    });
                                    setShowFinalOutputModal(true);
                                  }
                                }}
                                className="text-xs font-semibold text-blue-700 dark:text-blue-200 bg-blue-200 dark:bg-blue-800/60 px-2 py-1 rounded hover:bg-blue-300 dark:hover:bg-blue-800/80 transition-colors"
                                aria-label="View Final Output"
                              >
                                📄 View Final Output
                              </button>
                            )}
                            {hasDetails && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-cap-dark px-2 py-1 rounded border">
                                {isExpanded ? '▼ Click to collapse' : '▶ Click to expand details'}
                              </span>
                            )}
                          </div>
                          <span className="text-xs opacity-75 font-medium">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className={`mt-1 ${log.isFinalOutput ? 'font-semibold' : ''}`}>
                          {log.message}
                        </div>
                        
                        {/* Expanded Details */}
                        {isExpanded && hasDetails && (
                          <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                            {log.details && (
                              <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Details:</h4>
                                <div className="grid grid-cols-1 gap-2 text-xs">
                                  {Object.entries(log.details).map(([key, value]) => (
                                    <div key={key} className="flex">
                                      <span className="font-medium text-gray-600 dark:text-gray-400 w-24 flex-shrink-0 capitalize">
                                        {key.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                                      </span>
                                      <span className="flex-1 font-mono bg-white dark:bg-cap-dark px-2 py-1 rounded border">
                                        {key === 'fullOutput' || key === 'finalOutput' ? (
                                          <div className="max-h-32 overflow-y-auto whitespace-pre-wrap">
                                            {String(formatLogData(value))}
                                          </div>
                                        ) : key === 'allStepResults' ? (
                                          <div className="space-y-1">
                                            {Array.isArray(value) && value.map((step: any, idx: number) => (
                                              <div key={idx} className="text-xs border-l-2 border-gray-300 dark:border-gray-600 pl-2">
                                                <div><strong>Step {step.step}:</strong> {step.status} ({step.duration})</div>
                                                {step.output && (
                                                  <div className="text-gray-600 dark:text-gray-400 mt-1 max-h-16 overflow-y-auto">
                                                    {step.output.length > 100 ? step.output.substring(0, 100) + '...' : step.output}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="max-h-32 overflow-y-auto whitespace-pre-wrap">
                                            {String(formatLogData(value))}
                                          </div>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {log.rawData && (
                              <div className="mt-3 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Raw Data:</h4>
                                  <CopyRawDataButton data={log.rawData} formatLogData={formatLogData} />
                                </div>
                                <pre className="text-xs bg-gray-100 dark:bg-gray-700 p-3 rounded border max-h-40 overflow-auto whitespace-pre-wrap break-all">
                                  {String(formatLogData(log.rawData))}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Properties Panel */}
      {propertiesPanelOpen && (
        <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark overflow-y-auto">
          <PropertiesPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            agents={agents}
            models={models}
          />
        </div>
      )}

      {/* Version History Panel - matches Project History "Change History" styling */}
      {showVersionHistory && workflowId && currentProject && (
        <div className="w-[600px] border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-warm-white">Change History</h2>
              <button
                onClick={() => setShowVersionHistory(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <Tooltip content="Close">
                <X className="w-5 h-5" />
                </Tooltip>
              </button>
            </div>
            <EntityHistoryTab
              entityType="orchestration"
              entityId={parseInt(String(workflowId), 10)}
              projectId={parseInt(String(currentProject.project_id), 10)}
              highlightChangesSince={highlightChangesSince}
              compact={false}
              onEntityChanged={() => {
                if (workflowId) {
                  // AC-1406: Direct invalidation via the centralized
                  // queryKeys factory (no `loadWorkflow` shim). Invalidating
                  // `queryKeys.workflow(id)` cascades to the steps and
                  // execution sub-keys via RQ's prefix-match contract — see
                  // `queryKeys.ts` JSDoc.
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.workflow(workflowId),
                  });
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Final Output Modal */}
      {showFinalOutputModal && finalOutputData && (
        <OutputDisplayModal
          isOpen={showFinalOutputModal}
          onClose={() => setShowFinalOutputModal(false)}
          runResult={finalOutputData.runResult}
          title="Final Output"
          workflowSteps={nodes
            .filter(node => node.data?.step)
            .map(node => ({
              orchestration_step_id: node.data.step.orchestration_step_id,
              step_type: node.data.step.step_type,
              description: node.data.step.description,
              config: node.data.step.config,
              agent_id: node.data.step.agent_id,
              label: node.data.step.label,
            }))}
          allRunResults={executionLogs
            .filter(log => log.rawResult && log.isStepOutput)  // AC-503: Exclude final output log from step count
            .map(log => log.rawResult)}
          onNavigateToStep={handleNavigateToStep}
          sessionMessages={sessionMessages}
          sessionInfoMap={sessionInfoMap}
          agents={agents}
          onEditAgent={handleEditAgent}
        />
      )}

      {/* Log Management Panel */}
      {showLogManagementPanel && workflowId && (
        <LogManagementPanel
          workflowId={workflowId}
          isOpen={showLogManagementPanel}
          onClose={() => setShowLogManagementPanel(false)}
          onLogsDeleted={(deletedCount) => {
            console.log(`Deleted ${deletedCount} run instances`);
            // Refresh run history if it's loaded
            if (runHistory.length > 0) {
              loadRunHistory();
            }
          }}
        />
      )}

      {/* Output Display Modal */}
      <OutputDisplayModal
        isOpen={showOutputModal}
        onClose={() => setShowOutputModal(false)}
        runResult={selectedRunResult}
        title="Step Output Details"
        workflowSteps={nodes
          .filter(node => node.data?.step)
          .map(node => ({
            orchestration_step_id: node.data.step.orchestration_step_id,
            step_type: node.data.step.step_type,
            description: node.data.step.description,
            config: node.data.step.config,
            agent_id: node.data.step.agent_id,
            label: node.data.step.label,
          }))}
        allRunResults={executionLogs
          .filter(log => log.rawResult && log.isStepOutput)  // AC-503: Exclude final output log from step count
          .map(log => log.rawResult)}
        onNavigateToStep={handleNavigateToStep}
        sessionMessages={sessionMessages}
        sessionInfoMap={sessionInfoMap}
        agents={agents}
        onEditAgent={handleEditAgent}
      />

      {/* AC-465: Overlapping Runs Dialog */}
      <OverlappingRunsDialog
        isOpen={showOverlappingRunsDialog}
        onClose={() => setShowOverlappingRunsDialog(false)}
        onAction={handleOverlappingRunsAction}
        activeRuns={activeRuns}
        workflowName={workflowName || 'this workflow'}
      />

      {/* Workflow Summary Modal (AC-1161) */}
      {showSummaryModal && workflowSummary && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSummaryModal(false)}
        >
          <div
            className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-lg">📊</span>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Workflow Summary</h2>
              </div>
              <button
                onClick={() => setShowSummaryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto prose prose-sm dark:prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
                {workflowSummary}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowSummaryModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu for Nodes */}
      {contextMenuNode && contextMenuPosition && (
        <NodeContextMenu
          node={contextMenuNode}
          position={contextMenuPosition}
          onConvert={handleConvertToAutoPrompt}
          onConvertToFilter={handleConvertToFilter}
          onClose={handleContextMenuClose}
        />
      )}

      {/* Schedule Modal */}
      {workflowId && (
        <ScheduleModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            loadCurrentSchedule(); // Refresh schedule after modal closes
          }}
          workflowId={parseInt(workflowId, 10)}
        />
      )}

      {/* AC-1349: Agent edit modal opened from RunResultErrorPanel callout */}
      {editAgentTarget && (
        <AgentFormModal
          agent={agents.find(a => a.agent_id === editAgentTarget.agentId) ?? null}
          models={models}
          availableAgents={agents}
          focusField={editAgentTarget.focusField}
          onSubmit={handleEditAgentSubmit}
          onClose={() => setEditAgentTarget(null)}
        />
      )}
    </div>
  );
};

export default WorkflowEditor;